import _ from 'lodash'
import { v4 as uuid } from 'uuid'
import { Map, List, fromJS, isImmutable, isList } from 'immutable'
import { getElementDataRoot } from '../../constants/getElementDataRoot'
import { isInViewport } from '../../constants/isInViewport'
import { getValueForMode, getParentIdInModal, LAYOUT_MODE, getFirstParentElement } from '../../constants/toUnits'
import { clearPreviewUserData, getAxios, getPreviewDynamicData } from '../../lib/projectAxios'
import * as actionTypes from './actionTypes'
import { ftCloseModal } from './ftCloseModal'
import { processSlackMessage } from './processSlackMessage'
import { processSfLWCRedirect } from './processSfLWCRedirect'
import { collectAutoPDFState, processDocGen, processDocGenTitanSign } from './processDocGen'
import { processAIAction } from './processAIAction'
import { processButtonAction } from './processButtonAction'
import { processWidgetSFMapping } from './processWidgetSFMapping'
import { processWorkFlow } from './processWorkFlow'
import { processValueRules, recursiveChkParentVisible } from '../../lib/rules'
import { LIST_BASED_FIELDS } from '../../constants/LIST_BASED_FIELDS'
import { getMappingCount } from '../../constants/getMappingCount'
import { TAB_TYPES } from '../../lib/tab-utils'
import { ftRemoveLocalStorage, toJS, processActionsSFItem, updateTabValue } from '../../lib/common'
import { getNodeTypeLabel } from '../../lib/getNodeTypeLabel'
import { setElemValue } from './setElemValue'
import {
  getCalendarFileContent,
  getParams,
  getState,
  getStateValues,
  getParentRulesStatus,
  getValueByField,
  processRuleCondition,
  getProcessTargetsFunction,
  isTableRuleKey,
  isListRuleKey,
} from '../../lib/processPortalRules'
import { setVisibilityOfSFMappingElem } from '../../lib/setVisibilityOfSFMappingElem'
import { setTargetParamsValue } from './setTargetParamValue'
import { webProjectSmartvLogout } from '../../lib/auth'
import { getSessionProjectLocaKey } from '../../components/WidgetPreview/ProjectPersistSessionParameter'
import { getPageUID } from '../../lib/getValueByType'
import { doProcessRules, shallowEqual } from '../../lib/processPortalRules'
import { projectPreviewDetectLocationAPI } from './projectPreviewSetOnLoadData'
import { processApplyOnloadACRules } from '../../lib/processACRules'
import { processPaymentIntegration } from './processPaymentIntegration'
import { processSFAutoTrigger } from './processSFAutoTrigger'
import { openPrivateModal } from './openPrivateModal'
import { getRootParent } from './getRootParent'
import { IS_SERVER } from '../../constants/ssr'
import { processSurveyFinish } from './processSurveyFinish'
import { processSubmissionFlowNode } from './processSubmissionFlow'
import { processVirusScan } from './processVirusScan'
import { processAddressValidation } from './processAddressValidation'
import { recalculatePowerFields } from '../../lib/powerFields'
import { sfClearCacheData, sfFetchCacheData } from './pwa-tools'
import resizeBase64Img from '../../components/WidgetBuilder/UI/Components/ResizeBase64Img'
import { moveModalOnFocus } from './moveModalOnFocus'
import { SURVEY_EMAIL_TYPE, SURVEY_TYPE } from '../../constants/features'
import { SESSION_SALT_NAME, SESSION_TOKEN_NAME } from '../../constants/session'
import { SALESFORCE_HEADERS_OFFSET } from '../../constants/embeddedMode'
import { processSfPlatformPush } from './processSFEventPlatform'
import { addToCart } from './cart'
import { processOtpLogin } from './processOtpLib'
import { processOtpVerify } from './processOtpLib'
import { processTextReviewInteractivityAction } from './processCondCacheActions/processTextReviewInteractivityAction'
import { fetchEnvParameter } from './envParameters'
import { findParentScroll } from '../../lib/findParentScroll'
import { getElementPropsByUID } from '../../lib/getFieldByUID'
import { getValueByUID } from '../../lib/getValueByUID'
import { setValueByUID } from '../../lib/setValueByUID'
import { getSingleElementStateByIndex } from '../../lib/textReviewWidget'
import { openAddressValidationErrorMessageDialog } from './addressValidationErrorMessageDialog'
import { ACTION_TYPES } from '../../lib/constants/ActionTypes'
import { outputActionFlowSummary } from './utils'

const RESTRICT_SURVEY_NODES = [
  'TabInter',
  'formInteractivity',
  'stepInteractivity',
  'multiStepInteractivity',
  'accordionInteractivity',
  'carouselInteractivity',
  'continualFormInteractivity',
  'cartInteractivity',
  'tabInteractivity',
  'repeatInteractivity',
  'barcodeInteractivity',
  'docGen',
  'paymentIntegration',
  'ExportDownload',
  'VirusScan',
  'pageActions',
  'DrawerUI',
  'msgDlg',
  'msgDlgslack',
  'projectUI',
  'uac',
]

const RESTRICT_RUN_NODES = {
  [SURVEY_TYPE]: RESTRICT_SURVEY_NODES,
  [SURVEY_EMAIL_TYPE]: RESTRICT_SURVEY_NODES,
}

const CUSTOM_ACTION_TYPES = ['sendEmail']

const DEFAULT_SCROLL_INTO_VIEW_OPTIONS = {
  block: 'start',
  inline: 'nearest',
}

const RAF_CONTAINER_SCROLL_CSS_SELECTOR = '.ft--theme--RepeatAutoFit--scroll'

export const STATIC_COND_CACHE = [
  'redirect',
  'msgDlg',
  'confirmDlg',
  'redirectToCart',
  'redirectToProduct',
  'redirectToCheckout',
  'redirectToLogin',
  'redirectToRegistration',
  'redirectToCatalog',
  'redirectToPage',
  'redirectToProfile',
]

const axios = getAxios()

const shouldDisableRecalculation = (localAction) => [ACTION_TYPES.SET_BG_PASCAL_CASE, ACTION_TYPES.SET_BORDER_PASCAL_CASE, ACTION_TYPES.SET_COLOR_PASCAL_CASE].includes(localAction)

/**
 * This function scrolls a given element into view, allowing for a custom top margin (`scrollMarginTop`)
 * that defines the additional space (in pixels) between the viewport's top edge and the element when it
 * becomes visible. If `scrollMarginTop` is provided, it temporarily applies this margin to the element’s
 * `scroll-margin-top` style to create extra space above it when scrolled.
 *
 * Additionally, if the element is within a container matching the CSS selector `RAF_CONTAINER_SCROLL_CSS_SELECTOR`
 * (referred to here as `closestRAFContainer`), this container’s vertical overflow restriction is temporarily
 * removed. This ensures smooth scrolling by bypassing the overflow restriction, which would otherwise interfere
 * with the `scrollIntoView` behavior. The overflow is reset once the scrolling is complete.
 */
const scrollToFocusedElement = (element, scrollOptions = DEFAULT_SCROLL_INTO_VIEW_OPTIONS) => {
  let closestRAFContainer

  const { scrollMarginTop, ...scrollIntoViewOptions } = scrollOptions

  if (element && scrollMarginTop) {
    element.style.scrollMarginTop = `${scrollMarginTop}px`

    closestRAFContainer = element.closest(RAF_CONTAINER_SCROLL_CSS_SELECTOR)

    if (closestRAFContainer) {
      closestRAFContainer.style.overflowY = 'unset !important'
    }
  }

  element?.scrollIntoView(scrollIntoViewOptions)

  if (closestRAFContainer) {
    closestRAFContainer.style.overflowY = ''
  }
}

const set_focusAction = async (entry, getState, dispatch) => {
  let elemUID = entry.getIn(['payload', 'uid'], '')
  const index = entry.getIn(['payload', 'index'], '')
  const noScrollFix = entry.getIn(['payload', 'noScrollFix'])
  const noReveal = entry.getIn(['payload', 'noReveal'])
  const action = entry.getIn(['payload', 'action'], List())
  let actionOptions = entry.getIn(['payload', 'actionOptions'], Map())

  if (isList(elemUID)) {
    elemUID = elemUID.last()
  }

  let elements
  let state = getState()

  const isEmbeddedToSF = state.get('isEmbeddedToSF', false)
  const improveModalsMessagesDisplaying = state.getIn(['project', 'Settings', 'improveModalsMessagesDisplaying'], false)
  const elementScrollMarginTop = isEmbeddedToSF && improveModalsMessagesDisplaying ? SALESFORCE_HEADERS_OFFSET : 0

  if (state.getIn(['project', 'Settings', 'elements', elemUID])) {
    elements = state.getIn(['project', 'Settings', 'elements'])
  } else if (entry.getIn(['payload', 'pageID'], '')) {
    elements = state.getIn(['pages', entry.getIn(['payload', 'pageID'], ''), 'Props', 'elements'])
  } else if (state.getIn(['pages', state.get('page'), 'Props', 'elements', elemUID])) {
    elements = state.getIn(['pages', state.get('page'), 'Props', 'elements'])
  }
  if (Map.isMap(elements)) {
    const isInvalidParent = (uid) => {
      let elem = elements.get(uid)
      if (elem && elem.get('type') == 'CarouselContainer') {
        return true
      }
      if (elem && elem.get('parent')) {
        return isInvalidParent(elem.get('parent'))
      }
      return false
    }
    if (isInvalidParent(elemUID)) {
      return false
    }
  }
  try {
    let isCustomFocus = false
    const getDomElem = () => {
      let domElem
      if (elements.getIn([elemUID, 'type']) === 'Tab') {
        domElem = document.querySelector(`#${elemUID} button.ft--theme--Tab--tabBtnSelected`)
        if (!domElem) {
          domElem = document.querySelector(`#${elemUID} button.ft--theme--Tab--tabBtn`)
        }
      } else if (elements.getIn([elemUID, 'type']) === 'SmartVOtpForm') {
        domElem = document.querySelector(`#${elemUID} input`)
        return domElem
      } else {
        if (index !== undefined && index !== null) {
          domElem = document.getElementById(`control${elemUID}_${index}`)
        } else {
          domElem = document.getElementById('control' + elemUID)
        }
      }
      if (!domElem) {
        //for dynamic checkbox
        if (index !== undefined && index !== null) {
          domElem = document.getElementById(`chb${elemUID}${index}0`)
        } else {
          domElem = document.getElementById(`chb${elemUID}0`)
        }
      }
      if (!domElem && index !== undefined && index !== null) {
        domElem = document.getElementById('control' + elemUID)
      }
      if (!domElem && elements.getIn([elemUID, 'type']) === 'FormField') {
        let id = index !== undefined && index !== null ? `${elemUID}_${index}` : elemUID
        domElem = document.getElementById(id)
        isCustomFocus = !!domElem
      }
      return domElem
    }
    let domElem = getDomElem()
    if (!domElem && Map.isMap(elements)) {
      let firstParentId = getFirstParentElement(elements, elemUID)
      if (firstParentId) {
        let parentDomElem = document.getElementById(firstParentId)
        if (!parentDomElem && index !== undefined && index !== null) {
          parentDomElem = document.getElementById(`${firstParentId}${index}`)
        }
        if (parentDomElem) {
          scrollToFocusedElement(parentDomElem, {
            block: 'nearest',
            inline: 'nearest',
            scrollMarginTop: elementScrollMarginTop,
          })
          actionOptions = actionOptions.set('clientY', getNodePosition(parentDomElem, actionOptions.get('clientY')))
          setTimeout(() => {
            domElem = getDomElem()
            // console.log('After focus', domElem)
            if (domElem) {
              actionOptions = actionOptions.set('clientY', getNodePosition(domElem, actionOptions.get('clientY')))
              domElem.focus()
              moveModalOnFocus(state, domElem)
            }
          }, 300)
        }
      }
    }

    if (domElem) {
      if (isCustomFocus) {
        scrollToFocusedElement(domElem, {
          inline: 'start',
          scrollMarginTop: elementScrollMarginTop,
        })
      } else {
        scrollToFocusedElement(domElem, {
          scrollMarginTop: elementScrollMarginTop,
        })
      }
      domElem.focus()
      actionOptions = actionOptions.set('clientY', getNodePosition(domElem, actionOptions.get('clientY')))
      try {
        if (elements.getIn([elemUID, 'type']) === 'Tab') {
          const dummyInput = document.createElement('input')
          dummyInput.setAttribute('aria-hidden', true)
          if (domElem.nextSibling) {
            domElem.parentNode.insertBefore(dummyInput, domElem)
          } else {
            domElem.parentNode.appendChild(dummyInput)
          }
          dummyInput.focus()
          dummyInput.parentNode.removeChild(dummyInput)
        }
      } catch (error) {
        console.error(error)
      }
      setTimeout(() => {
        if (isCustomFocus) {
          scrollToFocusedElement(domElem, {
            inline: 'start',
            scrollMarginTop: elementScrollMarginTop,
          })
        } else {
          scrollToFocusedElement(domElem, {
            scrollMarginTop: elementScrollMarginTop,
          })
        }
        domElem.focus()
        moveModalOnFocus(state, domElem)
      }, 30)
    } else {
      if (elements.getIn([elemUID, 'type']) === 'SmartVOtpForm') {
        domElem = getDomElem()
        actionOptions = actionOptions.set('clientY', getNodePosition(domElem, actionOptions.get('clientY')))
        const tryFocusElement = (i) => {
          domElem = getDomElem()
          if (i < 10) {
            if (domElem) {
              scrollToFocusedElement(domElem, {
                scrollMarginTop: elementScrollMarginTop,
              })
              domElem.focus()
              setTimeout(() => {
                scrollToFocusedElement(domElem, {
                  scrollMarginTop: elementScrollMarginTop,
                })
                domElem.focus()
                moveModalOnFocus(state, domElem)
              }, 30)
            } else {
              setTimeout(tryFocusElement, 150, i + 1)
            }
          }
        }
        setTimeout(tryFocusElement, 300, 0)
      } else {
        domElem = document.getElementById(elemUID)
        const tryFocusElement = (i) => {
          domElem = document.getElementById(elemUID)
          if (i < 10) {
            if (domElem) {
              scrollToFocusedElement(domElem, {
                scrollMarginTop: elementScrollMarginTop,
              })
              domElem.focus()
              setTimeout(() => {
                scrollToFocusedElement(domElem, {
                  scrollMarginTop: elementScrollMarginTop,
                })
                domElem.focus()
                moveModalOnFocus(state, domElem)
              }, 30)
            } else {
              setTimeout(tryFocusElement, 150, i + 1)
            }
          }
        }
        setTimeout(tryFocusElement, 300, 0)
      }
    }
    const fixScroll = (i) => {
      let el = document.getElementById('ft__builder__root__view')
      if (el) {
        el.style.overflow = 'auto'
        setTimeout(() => {
          el.style.overflow = 'visible'
          if (i <= 4) {
            setTimeout(fixScroll, 10, i + 1)
          }
        }, 10)
      }
    }
    if (!noScrollFix) {
      setTimeout(fixScroll, 10, 0)
      setTimeout(() => {
        let domPage = document.getElementsByClassName('ft--theme--Page--root')
        if (domPage[0]) {
          domPage = domPage[0]
          domPage.style.overflow = 'auto'
          setTimeout(() => {
            domPage.style.overflow = 'visible'
          }, 80)
        }
      }, 80)
    }
  } catch (err) {
    console.error(err)
  }
  if (!noReveal) {
    dispatch(revealInTabsContainer(elemUID, index))
  }
  if (action?.size > 0) {
    let retState = getState()
    retState = retState.setIn(
      ['__cache__'],
      retState.getIn(['__cache__'], List()).push(
        fromJS({
          type: 'runAsyncAction',
          payload: {
            action: action,
            index: index,
            options: actionOptions,
          },
        })
      )
    )
    dispatch({
      type: actionTypes.SET_STORE,
      payload: {
        store: retState,
      },
    })
  }
}

const StepAction = async (entry, getState, dispatch) => {
  const uid = entry.getIn(['payload', 'uid'], '')
  const action = entry.getIn(['payload', 'action'], List())

  if (entry.getIn(['payload', 'isProcessValidation'])) {
    try {
      const elements = entry.getIn(['payload', 'isProcessValidation', 'elements'], List())
      const index = entry.getIn(['payload', 'isProcessValidation', 'index'], List())
      const value = entry.getIn(['payload', 'isProcessValidation', 'value'], Map())
      const scroll = entry.getIn(['payload', 'isProcessValidation', 'scroll'])
      let state = getState()
      const fieldsValidationErrors = []
      elements.map((elem) => {
        const res = validateFieldWithIndex(elem, index, state, dispatch)
        const localValidation = res?.firstErrorField
        const firstErrorFieldIndex = res?.firstErrorFieldIndex
        if (localValidation) {
          fieldsValidationErrors.unshift({
            status: true,
            uid: localValidation,
            index: firstErrorFieldIndex,
            elem: elem,
            state: state,
          })
        }
      })
      fieldsValidationErrors.sort((a, b) => a.index - b.index)

      // In case of field validation errors, dispatch an event to set focus on the first invalid element
      // and prevent proceeding to the next step.
      if (fieldsValidationErrors.length > 0) {
        const firstError = fieldsValidationErrors[0]
        const data = fromJS({
          type: 'set_focus',
          payload: {
            uid: firstError.uid,
            index: firstError.index,
            noScrollFix: true,
            noReveal: null,
            action: null,
          },
        })
        state = state.setIn(['__cache__'], state.getIn(['__cache__'], List())?.push(data))
        state = state.setIn(['fieldsValidationErrors'], fromJS(fieldsValidationErrors))

        dispatch({
          type: actionTypes.SET_STORE,
          payload: {
            store: state,
          },
        })
        return
      } else {
        state = state.setIn(['fieldsValidationErrors'], fromJS([]))
        dispatch({
          type: actionTypes.SET_STORE,
          payload: {
            store: state,
          },
        })
      }

      if (value.get('path') && isImmutable(value.get('path'))) {
        state = state.setIn(value.get('path').toJS(), value.get('value'))
        state = doProcessRules(
          state,
          false,
          null,
          [],
          [_.tail(value.get('path').toJS() || [])],
          false,
          false,
          state.get('prevRulesState', Map()).toJS()
        )
      }
      if (scroll) {
        state = state.setIn(
          ['__cache__'],
          state.getIn(['__cache__'], List()).push(
            fromJS({
              type: 'StepScrollAction',
              payload: {
                scroll: scroll,
                uid: uid,
                value: value.getIn(['value', 'ftListValue']),
                index,
              },
            })
          )
        )
      }
      dispatch({
        type: actionTypes.SET_STORE,
        payload: {
          store: state,
        },
      })
    } catch (err) {
      console.error(err)
    }
  }
  dispatch(processButtonAction(action, uid, false, null))
}

const hasValidationErrors = (state) => {
  const fieldsValidationErrors = state.getIn(['fieldsValidationErrors'], List())?.toJS()
  return fieldsValidationErrors.length > 0
}

const StepScrollAction = async (entry, getState, dispatch) => {
  let state = getState()

  if (hasValidationErrors(state)) {
    return
  }
  const code = entry.getIn(['payload', 'scroll'], '')
  if (code === 'Header') {
    let elem = document.getElementById('Header')
    if (elem) {
      elem.scrollIntoView()
      return
    } else {
      elem = document.getElementById('ft__builder__root__el')
      if (elem) {
        elem.scrollTop = 0
      }
      return
    }
  }
  const pageUID = state
    .getIn(['project', 'Pages'], List())
    .find((page) => page.get('_id') === state.get('page'))
    ?.get?.('uid')
  const uid = entry.getIn(['payload', 'uid'], '')
  const index = entry.getIn(['payload', 'index'])
  let stepValue = entry.getIn(['payload', 'value'], '')

  const RepeatedStripsID = state.getIn(['project', 'Settings', 'RepeatedStripsID'], List())
  const elements = state.hasIn(['project', 'Settings', 'elements', uid])
    ? state.getIn(['project', 'Settings', 'elements'], Map())
    : state.getIn(['pages', state.get('page'), 'Props', 'elements'], Map())
  const parentStripID = getRootParent(uid, elements)
  const parentBeforeStrip = getRootParent(uid, elements, false, true)
  const parentElem = state.hasIn(['project', 'Settings', 'elements', parentStripID])
    ? state.getIn(['project', 'Settings', 'elements', parentStripID], Map())
    : state.getIn(['pages', state.get('page'), 'Props', 'elements', parentStripID], Map())
  const component = state.hasIn(['project', 'Settings', 'elements', uid])
    ? state.getIn(['project', 'Settings', 'elements', uid], Map())
    : state.getIn(['pages', state.get('page'), 'Props', 'elements', uid], Map())
  const isRepeatCol = parentElem.getIn(['props', 'isRepeatCol'], false)
  const isRepeat = component.getIn(['props', 'isRepeat'], false)
  let colName = component.getIn(['props', state.get('layoutMode', 'lg'), 'gridArea'])
  if (!colName && parentBeforeStrip && parentBeforeStrip != uid) {
    colName = state.getIn([
      'pages',
      state.get('page'),
      'Props',
      'elements',
      parentBeforeStrip,
      'props',
      state.get('layoutMode', 'lg'),
      'gridArea',
    ])
  }
  let timeout = 500
  if (!stepValue) {
    if (isRepeat || isRepeatCol) {
      stepValue = state.getIn(['state', pageUID, parentStripID, uid, 0, 'ftListValue'])
    } else {
      stepValue = state.getIn(['state', pageUID, uid, 'ftListValue'])
    }
    timeout = 0
  }
  if (stepValue) {
    let selector = `#${uid} [id="step-${stepValue}"]`
    if (component && code === 'Top') {
      switch (component.get('type')) {
        case 'Tab':
          selector = `#${uid} .ft--theme--Tab--tabPanel`
          break
        case 'Stepper':
          selector = `#${uid} .ft--theme--Stepper--panelRoot`
          break
      }
    } else if (component && code === 'Bottom') {
      switch (component.get('type')) {
        case 'Tab':
          selector = `#${uid} .ft--theme--Tab--tabContent[data-tab='${stepValue}']`
          break
        case 'Stepper':
          selector = `#${uid}`
          break
      }
    }
    let stepElem = document.querySelector(selector)
    if (RepeatedStripsID.includes(parentStripID) && index !== null && index !== undefined) {
      let key = isRepeatCol
        ? `#${parentStripID}-${colName}i${index} #step-${stepValue}`
        : `#${parentStripID}${index} #step-${stepValue}`
      stepElem = document.querySelector(key)
    }
    if (!stepElem) {
      if (RepeatedStripsID.includes(parentStripID) && index !== null && index !== undefined) {
        stepElem = document.querySelector(
          isRepeatCol
            ? `#${parentStripID}-${colName}i${index} #${uid} .ft--theme--MultiStepper--stepContent[data-tab="${stepValue}"]`
            : `#${parentStripID}${index} #${uid} .ft--theme--MultiStepper--stepContent[data-tab="${stepValue}"]`
        )
      } else {
        stepElem = document.querySelector(`#${uid} .ft--theme--MultiStepper--stepContent[data-tab="${stepValue}"]`)
      }
    }
    if (stepElem) {
      if (code === 'Top') {
        dispatch({
          type: actionTypes.SET_STORE,
          payload: {
            store: state.setIn(
              ['__cache__'],
              state.getIn(['__cache__'], List()).push(
                fromJS({
                  type: 'set_focus',
                  payload: {
                    uid: uid,
                    index: null,
                    noScrollFix: true,
                  },
                })
              )
            ),
          },
        })
      } else if (code === 'Bottom') {
        try {
          const parentScroll = findParentScroll(stepElem)
          if (parentScroll) {
            const parentRect = parentScroll.getBoundingClientRect()
            const elemRect = stepElem.getBoundingClientRect()
            let topDelta = elemRect.top - parentRect.top + parentScroll.scrollTop
            parentScroll.scrollTop = topDelta + stepElem.clientHeight - parentScroll.clientHeight + 40
            return null
          }
        } catch (error) {
          console.error(error)
        }

        setTimeout(() => {
          let el = document.getElementById('ft__builder__root__el')
          let end = 0

          stepElem = document.querySelector(selector)
          if (stepElem) {
            end = stepElem?.getBoundingClientRect?.()?.bottom ?? 0
          } else {
            stepElem = document.getElementById(parentStripID)
            end = stepElem?.getBoundingClientRect?.()?.bottom ?? 0
          }
          el.scrollTop = end
        }, timeout)
      }
      return
    }
  }
  try {
    let elem = document.getElementById(uid)

    if (RepeatedStripsID.includes(parentStripID) && index !== null && index !== undefined) {
      let key = isRepeatCol ? `#${parentStripID}-${colName}i${index} #${uid}` : `#${parentStripID}${index} #${uid}`
      elem = document.querySelector(key)
    }
    if (elem && code === 'Top') {
      const _isInViewport = isInViewport(elem)
      if (!_isInViewport) {
        elem.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
      return
    }
    let el = document.getElementById('ft__builder__root__el')

    if (el) {
      if (code === 'Top') {
        el.scrollTop = 0
      } else if (code === 'Bottom') {
        let end = 0
        if (el && el.firstChild) {
          end = el.firstChild.getBoundingClientRect()?.height ?? 0
        }
        el.scrollTop = end
      }
    }
  } catch (err) {
    console.error(err)
  }
}

const getNodePosition = (node, defaultClientY) => {
  try {
    if (node) {
      let rect = node.getBoundingClientRect()
      if (rect && rect.top) {
        return rect.top
      }
    }
  } catch (err) {}
  return defaultClientY
}

const getElementFromState = (uid, root = [], state = Map()) => state.getIn([...root, 'elements', uid], Map())

export const revealInTabsContainer = (uid, index) => (dispatch, getState) => {
  try {
    let state = getState()
    const isPreview = true
    let isProject = state.hasIn(['project', 'Settings', 'elements', uid, 'type'])
    const PageID = state.get('page')
    const layoutMode = 'lg'
    const path = getElementDataRoot(isProject ? 'project' : PageID)
    const elements = state.getIn([...path, 'elements'], Map())

    let parent = elements.getIn([uid, 'parent'])
    let prevParent = uid
    let tabID = null
    while (parent) {
      let type = elements.getIn([parent, 'type'])
      if (TAB_TYPES.includes(type)) {
        tabID = parent
        break
      } else {
        prevParent = parent
        parent = elements.getIn([parent, 'parent'])
      }
    }

    const pageUID = state
      .getIn(['project', 'Pages'], List())
      .find((page) => page.get('_id') === state.get('page'))
      ?.get?.('uid')

    if (tabID) {
      let parentID = isProject ? 'project' : pageUID
      let metaInfo = state.getIn(['metaInfo', parentID, tabID], Map())
      let metaChoices = state.getIn(['meta', parentID, tabID], List())

      let isRepeat = false
      let repeatUID = null

      const parent = getParentIdInModal(state, elements.getIn([tabID, 'parent']), isProject)
      const parentType = state.getIn([
        ...getElementDataRoot(PageID),
        'elements',
        elements.getIn([tabID, 'parent']),
        'type',
      ])

      const isRepeatedStrip = state.getIn(['project', 'Settings', 'RepeatedStripsID'], List()).toJS()

      if (parentType === 'Strip' && isRepeatedStrip.includes(parent)) {
        isRepeat = true
        repeatUID = parent
      }

      let tabRules = state.getIn(
        isRepeat ? ['rules', parentID, repeatUID, `${tabID}-tab`, index] : ['rules', parentID, , `${tabID}-tab`],
        Map()
      )

      let component = elements.get(tabID, Map())
      const isStatic = component.getIn(['props', 'source'], 'static') === 'static'
      let choices
      let tabs = toJS(updateTabValue(getValueForMode(component, layoutMode, ['props'], ['tabs'], List())))
      if (!_.isArray(tabs)) {
        tabs = []
      }
      if (isStatic && !isPreview) {
        choices = tabs
      } else if (isStatic && !metaInfo.get('isDynamic')) {
        choices = tabs
      } else {
        const children = _.get(_.first(tabs), 'children', []) || []
        choices = metaChoices
          .map((v) => ({
            label: v.get('label'),
            id: v.get('value') || v.get('id'),
            ftValue: v.get('ftValue') || v.get('value') || v.get('id'),
            children,
          }))
          .toJS()
      }
      if (
        isPreview &&
        (component.getIn(['props', layoutMode, 'isRandomize']) || component.getIn(['props', layoutMode, 'isDistinct']))
      ) {
        const children = _.get(tabs, [0, 'children'], []) || []
        choices = _.uniqBy(
          metaChoices
            .map((v) => ({
              label: v.get('label'),
              id: v.get('value') || v.get('id'),
              ftValue: v.get('ftValue') || v.get('value') || v.get('id'),
              children,
            }))
            .toJS(),
          'id'
        )
      }

      choices = _.filter(choices, (tab) => {
        if (tab) {
          if (
            ((tab.hide && !tabRules.get(tab?.id, List()).includes('show')) ||
              tabRules.get(tab?.id, List()).indexOf('hide') !== -1) &&
            tabRules.get(tab?.id, List()).indexOf('show') === -1
          ) {
            return false
          } else {
            return true
          }
        } else {
          return false
        }
      })

      let tabIndex = _.findIndex(tabs, (tab) => _.includes(tab.children, prevParent))
      let tab = tabs[tabIndex]

      if (tabIndex !== -1 && tabID) {
        state = state.setIn(
          ['state', parentID, ...(isRepeat ? [repeatUID] : []), tabID, ...(isRepeat ? [index] : [])],
          fromJS({
            ftListValue: tab.ftValue,
            ftListLabel: tab.label,
            ftIsFirst: tabIndex === 0,
            ftIsLast: tabIndex === _.size(tabs) - 1,
          })
        )

        dispatch({
          type: actionTypes.SET_STORE,
          payload: {
            store: state,
          },
        })
      }
    }
  } catch (error) {
    console.error(error.stack)
  }
}

const processSendCustomAction = async (cache, siteState, dispatch) => {
  let onError = List()
  let onSuccess = List()
  try {
    const postState = {
      actions: {},
    }
    let form = new FormData()
    const buildCalendarFiles = []
    let sendOptions = {}
    let hashAppendFiles = {}
    let isSendAutoPdfState = false
    cache.map((conf) => {
      onError = onError.concat(conf.getIn(['payload', 'onError'], List()))
      onSuccess = onSuccess.concat(conf.getIn(['payload', 'onSuccess'], List()))
      const options = conf.getIn(['payload', 'options'], Map()).toJS()
      const uid = conf.getIn(['payload', 'uid'])
      const state = conf.getIn(['payload', 'state'], Map()).toJS()
      sendOptions = {
        ...sendOptions,
        ...options,
      }
      postState.actions[uid] = {
        state,
      }
      const files = conf.getIn(['payload', 'files'], List()).toJS()
      const docGen = conf.getIn(['payload', 'docGen'], List()).toJS()

      if (!siteState.getIn(['project', 'Settings', 'store', 'public', uid, 'isRunInBackground'])) {
        dispatch({
          type: actionTypes.SHOW_LOADING_WINDOW,
          payload: {
            messages: [],
          },
        })
      }

      _.each(docGen, (uid) => {
        let keys = _.keys(options)
        let keyValue = null
        let key = null

        if (siteState.getIn(['project', 'Settings', 'store', 'public', uid, 'type']) === 'AUTO_PDF') {
          isSendAutoPdfState = true
        }

        if (siteState.getIn(['widgets', 'PreviewDocGen', 'params', 'actionID']) == uid) {
          if (keys.includes('ftDocGenEditDocx')) {
            keyValue = _.get(options, ['ftDocGenEditDocx'])
            key = 'ftDocGenEditDocx'
          } else if (keys.includes('ftDocGenEditPdf')) {
            keyValue = _.get(options, ['ftDocGenEditPdf'])
            key = 'ftDocGenEditPdf'
          }
        }
        if (keyValue) {
          form.append(uid, keyValue)
          form.append(keyValue, _.get(window, ['__FT_PROJECT__API__FILES__', key, keyValue]))
        }
      })
      form.append('activeDocGenEdit', siteState.getIn(['widgets', 'PreviewDocGen', 'params', 'actionID']))

      _.each(files, (field) => {
        if (
          _.startsWith(field, 'calendar##') ||
          siteState.getIn(['project', 'Settings', 'store', 'public', field, 'type']) === 'CAL_EVENT_ICS'
        ) {
          let [__, calID] = String(field).includes('##') ? String(field).split('##') : [1, field]
          const getCal = async (calID) => {
            let file = await getCalendarFileContent(calID, siteState)
            let blob = new Blob([file], { type: 'text/calendar;charset=utf-8;' })
            form.append(field, blob, 'calendar.ics')
          }
          buildCalendarFiles.push(getCal(calID))
        }
        if (window.__FT_PROJECT__API__ && typeof window.__FT_PROJECT__API__.getFilesByID == 'function') {
          if (!hashAppendFiles[field]) {
            hashAppendFiles[field] = true
            let files = window.__FT_PROJECT__API__.getFilesByID(_.last(String(field).split(':')))
            _.each(files, function (file, index) {
              form.append(field + '___' + index, file)
            })
          }
        }
      })
    })
    if (buildCalendarFiles.length > 0) {
      await Promise.all(buildCalendarFiles)
    }
    if (_.isEmpty(postState.actions)) {
      return null
    }
    form.append('actions', JSON.stringify(postState.actions))
    if (isSendAutoPdfState) {
      form.append('autoPdfState', JSON.stringify(collectAutoPDFState(siteState)))
    }
    _.each(sendOptions, (value, key) => {
      form.append(key, JSON.stringify(value))
    })
    if (window.lastSendEmailTime) {
      let now = new Date().getTime() / 1000
      window.lastSendEmailTime = _.filter(window.lastSendEmailTime, (time) => now - time < 60)

      if (_.isArray(window.lastSendEmailTime) && window.lastSendEmailTime.length > 20) {
        console.error('Send email rate limit')
        dispatch({ type: actionTypes.CLOSE_LOADING_WINDOW })
        return false
      }
    }

    window.lastSendEmailTime = window.lastSendEmailTime || []
    window.lastSendEmailTime.push(new Date().getTime() / 1000)
    const { data, status } = await axios.post('/webprojects/customAction', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })

    dispatch({ type: actionTypes.CLOSE_LOADING_WINDOW })

    if (status === 200) {
      if (onSuccess.size > 0) {
        dispatch(processButtonAction(onSuccess, 'email-onSuccess', false, null))
      }
      // console.log('processSendCustomAction >>> request successful', data)
    } else {
      console.log('processSendCustomAction >>> something went wrong', data)
      if (onError.size > 0) {
        dispatch(processButtonAction(onError, 'email-onError', false, null))
      }
    }
  } catch (error) {
    console.log('processSendCustomAction >>> something went wrong', error)
    dispatch({ type: actionTypes.CLOSE_LOADING_WINDOW })
    if (onError.size > 0) {
      dispatch(processButtonAction(onError, 'email-onError', false, null))
    }
  }
}

const validateFieldWithIndex = (fieldUID, index, state, dispatch) => {
  let firstErrorField = null
  let firstErrorFieldIndex = null
  let hashRules = {}
  if (fieldUID && String(fieldUID).indexOf('ft_search') === -1) {
    fieldUID = String(fieldUID).split(':')
    let pageUID = fieldUID[0]
    let elemUID = fieldUID[1]
    let repeatRoot = state.getIn(['project', 'Settings', 'RepeatedStripsID'], List()).toJS()
    if ((index !== undefined && index !== null) || repeatRoot.indexOf(fieldUID[1]) > -1) {
      elemUID = fieldUID[2]
    }
    let currentPageUID = state
      .getIn(['project', 'Pages'], List())
      .find((page) => page.get('_id') === state.get('page'))
      ?.get?.('uid')
    let pageID = fieldUID[0] === currentPageUID ? state.get('page') : 'project'
    let root = getElementDataRoot(pageID)
    let component = state.getIn([...root, 'elements', elemUID], Map())
    const parentStatus = getParentRulesStatus(
      component.get('parent'),
      pageID,
      pageUID,
      index,
      elemUID,
      fieldUID[1],
      state
    )
    let localPath = ['formFieldStatuses', pageUID, elemUID]
    let rulesPath = ['rules', pageUID, elemUID]

    const uid = elemUID
    const control = component.getIn(['props', 'control'])
    const parent = component.getIn(['parent'])
    let isListCheckbox = false
    if (control == 'checkbox') {
      isListCheckbox = component.getIn(['props', 'source'], 'single') !== 'single'
    }
    let parentElem = getParentIdInModal(state, component.get('parent'), pageUID === 'project')
    const isRepeat = repeatRoot.indexOf(fieldUID[1]) !== -1
    const repeatUID = fieldUID[1]
    const getRepeatPath = (prefix = 'state', isFullValue) => {
      let propName = []
      if (control === 'currency' && prefix != 'formFieldStatuses') {
        propName = ['value']
      }
      if ((LIST_BASED_FIELDS.indexOf(control) > -1 || isListCheckbox) && prefix == 'state' && !isFullValue) {
        propName = ['ftListValue']
      }
      return pageUID === 'project'
        ? isRepeat
          ? [prefix, parentElem, repeatUID, uid, ...propName, String(index)]
          : [prefix, parentElem, uid, ...propName]
        : isRepeat
          ? [prefix, pageUID, repeatUID, uid, ...propName, String(index)]
          : [prefix, pageUID, uid, ...propName]
    }

    if (repeatRoot.indexOf(fieldUID[1]) === -1) {
      let repData = state.getIn(['state', pageUID], Map())
      if (isImmutable(repData)) {
        repData = repData.toJS()
      } else {
        repData = {}
      }

      let rulesPath = ['rules', pageUID, fieldUID[1]]
      let dataPath = [fieldUID[1]]
      if (fieldUID.length == 4 && component.getIn(['props', 'control']) !== 'signature') {
        dataPath.push(fieldUID[2])
      }
      const rulesStatus = state.getIn(rulesPath, List())
      let rules = List.isList(rulesStatus) ? rulesStatus.toJS() : []
      let value
      if (component.get('type') === 'FormField' && component.getIn(['props', 'control']) === 'daterange') {
        value = _.get(repData, [fieldUID[1], 'endDate'])
      } else if (component.get('type') === 'FormField' && component.getIn(['props', 'control']) === 'lookup') {
        value = _.get(repData, [fieldUID[1]])
      } else {
        value = _.get(repData, dataPath)
      }
      if (rules.indexOf('hide') === -1 && parentStatus.indexOf('hide') === -1) {
        let ruleStatus = dispatch(
          processValueRules(
            elemUID,
            component,
            getRepeatPath,
            null,
            value,
            rulesPath,
            'sfValidation',
            state.getIn(localPath)
          )
        )

        //@note error for visible elements
        if (ruleStatus.status) {
          if (!firstErrorField) {
            firstErrorField = fieldUID[1]
          }
          hashRules[elemUID] = ruleStatus.error
        }
      }
    } else {
      const rulesStatus = state.getIn(rulesPath, List())
      let rules = List.isList(rulesStatus) ? rulesStatus.toJS() : []
      const processCheckValid = (index) => {
        const getRepeatPath = (prefix = 'state', isFullValue) => {
          let propName = []
          if (control === 'currency' && prefix != 'formFieldStatuses') {
            propName = ['value']
          }
          if ((LIST_BASED_FIELDS.indexOf(control) > -1 || isListCheckbox) && prefix == 'state' && !isFullValue) {
            propName = ['ftListValue']
          }
          return pageUID === 'project'
            ? isRepeat
              ? [prefix, parentElem, repeatUID, uid, ...propName, String(index)]
              : [prefix, parentElem, uid, ...propName]
            : isRepeat
              ? [prefix, pageUID, repeatUID, uid, ...propName, String(index)]
              : [prefix, pageUID, uid, ...propName]
        }
        let ruleStatus
        let value = undefined
        let localIndex = undefined
        let localPath2 = [...localPath]
        const singleParentStatus = getParentRulesStatus(
          component.get('parent'),
          pageID,
          pageUID,
          index,
          elemUID,
          fieldUID[1],
          state
        )

        if (index !== undefined && index !== null) {
          localIndex = index
          rulesPath = ['rules', pageUID, fieldUID[1], fieldUID[2], index]
          let dataPath = [fieldUID[2]]
          if (fieldUID.length == 4 && component.getIn(['props', 'control']) !== 'signature') {
            dataPath.push(fieldUID[3])
          }
          dataPath.push(index)
          let repData = state.getIn(['state', pageUID, fieldUID[1]], Map())
          if (repData && isImmutable(repData)) {
            repData = repData.toJS()
          } else {
            repData = {}
          }
          if (component.get('type') === 'FormField' && component.getIn(['props', 'control']) === 'daterange') {
            value = _.get(repData, [fieldUID[2], localIndex, 'endDate'])
          } else if (component.get('type') === 'FormField' && component.getIn(['props', 'control']) === 'lookup') {
            value = _.get(repData, [fieldUID[2], localIndex])
          } else if (component.get('type') === 'TextReview') {
            const elementUID = fieldUID.at(-1)
            const repeatElementState = _.get(repData, elementUID)

            value = getSingleElementStateByIndex(repeatElementState, localIndex)
          } else {
            value = _.get(repData, dataPath)
          }
          localPath2 = ['formFieldStatuses', pageUID, fieldUID[1], fieldUID[2], String(localIndex)]
        }
        if (
          rules.indexOf('hide') === -1 &&
          parentStatus.indexOf('hide') === -1 &&
          singleParentStatus.indexOf('hide') === -1
        ) {
          ruleStatus = dispatch(processValueRules(elemUID, component, getRepeatPath, localIndex, value, rulesPath))

          //@note error for visible elements
          if (ruleStatus.status) {
            if (!firstErrorField) {
              firstErrorField = fieldUID
              firstErrorFieldIndex = localIndex
            }
            hashRules[elemUID] = ruleStatus.error
          }
        }
      }
      if (index === undefined || index === null) {
        const allowEmptyRow = state.getIn([
          'pages',
          state.get('page'),
          'Props',
          'elements',
          fieldUID[1],
          'props',
          'allowEmptyRow',
        ])
        let repData = state.getIn(['state', pageUID, fieldUID[1]], Map())
        if (repData && isImmutable(repData)) {
          repData = repData.toJS()
        } else {
          repData = {}
        }
        let count = getMappingCount(repData)
        if (allowEmptyRow && count < 1) {
          count = 1
        }
        _.times(count, processCheckValid)
      } else {
        processCheckValid(index)
      }
    }
  }
  return { firstErrorField, firstErrorFieldIndex }
}

const validateField = (fieldUID, index, state, dispatch) =>
  validateFieldWithIndex(fieldUID, index, state, dispatch)?.firstErrorField

const processValidateAction =
  (payload = {}) =>
  (dispatch, getAppState) => {
    let state = getAppState()
    const getRootParent = (uid, root) => {
      const parent = state.getIn([...root, 'elements', uid, 'parent'])
      if (parent) {
        let parentType = state.getIn([...root, 'elements', parent, 'type'])
        if (parentType === 'Strip' || parentType === 'RepeatAutoFit') {
          return parent
        } else {
          return getRootParent(parent, root)
        }
      } else {
        return null
      }
    }

    const getPositionOfElem = (options, uid) => {
      try {
        let result = options || {}
        let elem = document.getElementById(uid)
        if (elem) {
          let rect = elem.getBoundingClientRect()
          if (rect && rect.top) {
            result.clientY = rect.top
            return result
          }
        }
      } catch (err) {}
      return options
    }

    const collectChildID = (root, id, processedHash = [], isAny = false) => {
      let elem = state.getIn([...root, 'elements', id], Map())
      let child = []
      if (processedHash.includes(id)) {
        return child
      }
      if (elem.get('type') === 'Form') {
        _.each(LAYOUT_MODE, (mode) => {
          const childList = elem.getIn(['props', mode, 'children'], Map())
          childList
            .sortBy((val) => val.get('index'))
            .map((row) => {
              row.get('children', Map()).map((col) => {
                if (col && List.isList(col.get('children', List()))) {
                  child = [...child, ...col.get('children', List()).toJS()]
                }
              })
            })
        })
      } else if (TAB_TYPES.includes(elem.get('type'))) {
        _.each(LAYOUT_MODE, (mode) => {
          const tabs = elem.getIn(['props', mode, 'tabs'], List()).toJS()
          child = [...child, ..._.flatMap(tabs, (tab) => tab.children)]
        })
      } else {
        elem.get('children', List()).map((childList) => {
          if (List.isList(childList)) {
            child = [...child, ...childList.toJS()]
          } else {
            child.push(childList)
          }
        })
      }
      const FILTER_VALIDATE_TYPE = [
        'Strip',
        'Form',
        'Typography',
        'Button',
        'Link',
        'ItemList',
        'Breadcrumb',
        'Svg',
        'Line',
        'GoogleMap',
        'InteractiveDocument',
        'Image',
        'iframe',
        'YouTube',
        'Vimeo',
        'Widget',
        'Section',
        'Stack',
        'Scroll',
        'Progress',
        'FileViewer',
        'RepeatAutoFit',
        ...TAB_TYPES,
      ]
      // const allChildList = [
      //     ...child,
      //     ..._.flatMap(child, uid => collectChildID(root, uid, processedHash, isAny))
      // ]
      let allChildList = []
      _.each(child, (uid) => {
        allChildList = [...allChildList, uid, ...collectChildID(root, uid, processedHash, isAny)]
      })
      if (isAny) {
        return _.uniq(allChildList)
      }
      const allChild = _.filter(allChildList, (id) => {
        const elemJs = state.getIn([...root, 'elements', id])?.toJS()
        if (elemJs?.props?.data?.Type === 'ImageChoice') {
          return true
        }
        return (
          !_.startsWith(id, 'fit_') && !FILTER_VALIDATE_TYPE.includes(state.getIn([...root, 'elements', id, 'type']))
        )
      })
      return _.uniq(allChild)
    }

    let isInvalid = false
    let invalidIndex = payload?.index
    let firstElement
    let invalidChild = []
    const noReveal = payload?.noReveal
    switch (payload?.action) {
      case 'onCurrentStrip':
      case 'onPage': {
        const index = payload?.index
        if (payload && payload?.action === 'onPage') {
          payload.index = null
        }
        const currentId = payload.currentId
        let pageUID = payload?.pageUID
        let pageID = state
          .getIn(['project', 'Pages'], List())
          .find((page) => page.get('uid') === pageUID)
          ?.get('_id')
        if (!pageUID && !pageID) {
          pageID = state.get('page')
          state.getIn(['project', 'Pages'], List()).map((page) => {
            if (page.get('_id') === pageID) {
              pageUID = page.get('uid')
            }
          })
        }
        let root = ['pages', pageID, 'Props']
        let allChildren = []
        let stripList = state.getIn(['pages', pageID, 'Props', 'Grid'], List())
        if (payload?.action === 'onCurrentStrip') {
          let isProject = state.hasIn(['project', 'Settings', 'elements', currentId, 'type'])
          let root = getElementDataRoot(isProject ? 'project' : pageID)
          let component = state.getIn([...root, 'elements', currentId], Map())
          let parentID = component.get('parent')
          while (component.size > 0 && parentID && component.get('type') !== 'Strip') {
            parentID = component.get('parent')
            component = state.getIn([...root, 'elements', parentID], Map())
          }
          if (component.get('type') === 'Strip' && parentID) {
            stripList = fromJS([parentID])
          }
        }
        let childInRepeat = []

        stripList.map((uid) => {
          let allChildren = collectChildID(root, uid, [], true)
          const repeatAutoFit = _.filter(allChildren, (child) => {
            const elem = getElementFromState(child, root, state)
            return elem.get('type') === 'RepeatAutoFit'
          })
          _.each(repeatAutoFit, (fitID) => {
            let fitChildren = collectChildID(root, fitID)
            childInRepeat = [...childInRepeat, ...fitChildren, fitID]
          })
          stripList = fromJS([...stripList.toJS(), ...repeatAutoFit])
        })
        stripList.map((uid) => {
          const elem = getElementFromState(uid, root, state)
          if (elem.get('type') === 'Strip' || elem.get('type') === 'RepeatAutoFit') {
            const isRepeat =
              elem.getIn(['props', 'isRepeat']) ||
              elem.getIn(['props', 'isRepeatCol']) ||
              elem.get('type') === 'RepeatAutoFit'

            if (isRepeat) {
              let appState = getState(state)
              let repeatID = isRepeat ? [uid] : []
              let repStatePath = [pageUID, ...repeatID]
              let repState = _.get(appState, repStatePath, {})
              let count = getMappingCount(repState)
              if (elem.getIn(['props', 'allowEmptyRow']) && count === 0) {
                count = 1
              }
              if (payload.index !== null && payload.index !== undefined) {
                const rulesPath = ['rules', pageUID, uid, uid, String(payload.index)]
                const rules = state.getIn(rulesPath, List())
                if (List.isList(rules) && rules.includes('hide')) {
                  return null
                }

                allChildren = collectChildID(root, uid)
                _.each(allChildren, (uid) => {
                  const childRulesPath = ['rules', pageUID, ...repeatID, uid, String(payload.index)]
                  const childRules = state.getIn(childRulesPath, List())
                  if (List.isList(childRules) && childRules.includes('hide')) {
                    return null
                  }
                  let isInvisibleParent = recursiveChkParentVisible(
                    state.getIn(['pages', pageID, 'Props', 'elements']),
                    state.getIn(['pages', pageID, 'Props', 'elements', uid, 'parent']),
                    state.getIn(['rules', pageUID, ...repeatID]),
                    uid,
                    String(payload.index)
                  )
                  if (isInvisibleParent) {
                    return null
                  }

                  let path = [pageUID, ...repeatID, uid].join(':')
                  let localValidation = validateField(path, payload.index, state, dispatch)
                  if (localValidation) {
                    if (!firstElement) {
                      firstElement = uid
                      invalidIndex = index
                    }
                    invalidChild.push(uid)
                    isInvalid = true
                  }
                })
              } else {
                for (let i = 0; i < count; i++) {
                  const rulesPath = ['rules', pageUID, ...repeatID, uid, String(i)]
                  const rules = state.getIn(rulesPath, List())
                  if (List.isList(rules) && rules.includes('hide')) {
                    continue
                  }
                  allChildren = collectChildID(root, uid)

                  _.each(allChildren, (uid) => {
                    let isInvisibleParent = recursiveChkParentVisible(
                      state.getIn(['pages', pageID, 'Props', 'elements']),
                      state.getIn(['pages', pageID, 'Props', 'elements', uid, 'parent']),
                      state.getIn(['rules', pageUID, ...repeatID]),
                      uid,
                      String(i)
                    )
                    if (isInvisibleParent) {
                      return null
                    }
                    let path = [pageUID, ...repeatID, uid].join(':')
                    let localValidation = validateField(path, i, state, dispatch)
                    if (localValidation) {
                      if (!firstElement) {
                        firstElement = uid
                        invalidIndex = i
                      }
                      invalidChild.push(uid)
                      isInvalid = true
                    }
                  })
                }
              }
            } else {
              const rulesPath = ['rules', pageUID, uid]
              const rules = state.getIn(rulesPath, List())
              if (List.isList(rules) && rules.includes('hide')) {
                return null
              }

              allChildren = collectChildID(root, uid)
              _.each(allChildren, (uid) => {
                if (!childInRepeat.includes(uid)) {
                  let path = [pageUID, uid].join(':')
                  let localValidation = validateField(path, payload.index, state, dispatch)
                  if (localValidation) {
                    if (!firstElement) {
                      firstElement = uid
                      invalidIndex = payload.index
                    }
                    invalidChild.push(uid)
                    isInvalid = true
                  }
                }
              })
            }
          }
        })
        if (payload && payload?.action === 'onPage') {
          payload.index = index
        }
        if (isInvalid) {
          // if (_.size(payload.onInValid) > 0) {
          //     let opt = getPositionOfElem(payload.options, firstElement)
          //     dispatch(processButtonAction(fromJS(payload.onInValid), 'onInValid', typeof payload.index === 'number', payload.index, opt))
          // }
        } else {
          if (_.size(payload.onValid) > 0) {
            dispatch(
              processButtonAction(
                fromJS(payload.onValid),
                'onValid',
                typeof payload.index === 'number',
                payload.index,
                payload.options
              )
            )
          }
        }
        break
      }
      case 'onFieldGroup': {
        _.each(payload.elemList, (elemUIDPath) => {
          let elemPath = String(elemUIDPath).split(':')
          let elemUID = elemPath[1]
          let repeatRoot = state.getIn(['project', 'Settings', 'RepeatedStripsID'], List()).toJS()
          if (repeatRoot.includes(elemUID)) {
            elemUID = elemPath[2]
          }
          let pageID = state.get('page')
          let pageUID = state
            .getIn(['project', 'Pages'], List())
            .find((page) => page.get('_id') === pageID)
            ?.get?.('uid')
          let root = getElementDataRoot(elemPath[0] === pageUID ? pageID : 'project')

          const elem = state.getIn([...root, 'elements', elemUID], Map())
          if (elem.getIn(['props', 'control']) === 'lookup') {
            let additionalColumns = elem.getIn(['props', 'sf', 'mapping'], List()).map((item) => item.get('id'))
            if (additionalColumns.includes(_.last(elemPath))) {
              return true
            }
          }
          if (elem.get('type') === 'Strip') {
            const isRepeat = component.getIn(['props', 'isRepeat']) || component.getIn(['props', 'isRepeatCol'])
            if (isRepeat && payload.index !== null && payload.index !== undefined) {
              const rulesPath = ['rules', pageUID, elemUID, elemUID, String(payload.index)]
              const rules = state.getIn(rulesPath, List())
              if (List.isList(rules) && rules.includes('hide')) {
                return true
              }
            } else {
              const rulesPath = ['rules', pageUID, elemUID]
              const rules = state.getIn(rulesPath, List())
              if (List.isList(rules) && rules.includes('hide')) {
                return true
              }
            }
          } else {
            let parentID = elem.get('parent')
            let component = state.getIn([...root, 'elements', parentID], Map())
            while (component.size > 0 && parentID && component.get('type') !== 'Strip') {
              parentID = component.get('parent')
              component = state.getIn([...root, 'elements', parentID], Map())
            }
            if (component.get('type') === 'Strip' && parentID) {
              const isRepeat = component.getIn(['props', 'isRepeat']) || component.getIn(['props', 'isRepeatCol'])

              if (isRepeat && payload.index !== null && payload.index !== undefined) {
                const rulesPath = ['rules', pageUID, parentID, parentID, String(payload.index)]
                const rules = state.getIn(rulesPath, List())
                if (List.isList(rules) && rules.includes('hide')) {
                  return true
                }
              } else {
                const rulesPath = ['rules', pageUID, parentID]
                const rules = state.getIn(rulesPath, List())
                if (List.isList(rules) && rules.includes('hide')) {
                  return true
                }
              }
            }
          }

          let allChildren = [elemUID, ...collectChildID(root, elemUID)]
          _.each(allChildren, (uid) => {
            let path = repeatRoot.includes(elemPath[1])
              ? `${elemPath[0]}:${elemPath[1]}:${uid}`
              : `${elemPath[0]}:${uid}`
            let localValidation = validateField(path, payload.index, state, dispatch)
            if (localValidation) {
              if (!firstElement) {
                firstElement = uid
              }
              invalidChild.push(uid)
              isInvalid = true
            }
          })
        })
        if (isInvalid) {
          // if (_.size(payload.onInValid) > 0) {
          //     let opt = getPositionOfElem(payload.options, firstElement)
          //     dispatch(processButtonAction(fromJS(payload.onInValid), payload.currentId || 'onInValid', typeof payload.index === 'number', payload.index, opt))
          // }
        } else {
          if (_.size(payload.onValid) > 0) {
            dispatch(
              processButtonAction(
                fromJS(payload.onValid),
                payload.currentId || 'onValid',
                typeof payload.index === 'number',
                payload.index,
                payload.options
              )
            )
          }
        }
        break
      }
      case 'onField': {
        let elemPath = String(payload.elemUID).split(':')
        let elemUID = elemPath[1]
        let repeatRoot = state.getIn(['project', 'Settings', 'RepeatedStripsID'], List()).toJS()
        if (repeatRoot.includes(elemUID)) {
          elemUID = elemPath[2]
        }
        let pageID = state.get('page')
        let pageUID = state
          .getIn(['project', 'Pages'], List())
          .find((page) => page.get('_id') === pageID)
          ?.get?.('uid')
        let root = getElementDataRoot(elemPath[0] === pageUID ? pageID : 'project')
        let allChildren = [elemUID, ...collectChildID(root, elemUID)]
        const elem = state.getIn([...root, 'elements', elemUID], Map())
        if (elem.get('type') === 'Strip') {
          const isRepeat = component.getIn(['props', 'isRepeat']) || component.getIn(['props', 'isRepeatCol'])
          if (isRepeat && payload.index !== null && payload.index !== undefined) {
            const rulesPath = ['rules', pageUID, elemUID, elemUID, String(payload.index)]
            const rules = state.getIn(rulesPath, List())
            if (List.isList(rules) && rules.includes('hide')) {
              if (_.size(payload.onValid) > 0) {
                dispatch(
                  processButtonAction(
                    fromJS(payload.onValid),
                    'onValid',
                    typeof payload.index === 'number',
                    payload.index,
                    payload.options
                  )
                )
              }
              return
            }
          } else {
            const rulesPath = ['rules', pageUID, elemUID]
            const rules = state.getIn(rulesPath, List())
            if (List.isList(rules) && rules.includes('hide')) {
              if (_.size(payload.onValid) > 0) {
                dispatch(
                  processButtonAction(
                    fromJS(payload.onValid),
                    'onValid',
                    typeof payload.index === 'number',
                    payload.index,
                    payload.options
                  )
                )
              }
              return
            }
          }
        } else {
          let parentID = elem.get('parent')
          let component = state.getIn([...root, 'elements', parentID], Map())
          const repContainers = ['Strip', 'RepeatAutoFit']
          while (component.size > 0 && parentID && !repContainers.includes(component.get('type'))) {
            parentID = component.get('parent')
            component = state.getIn([...root, 'elements', parentID], Map())
          }
          if (repContainers.includes(component.get('type')) && parentID) {
            const isRepeat =
              component.getIn(['props', 'isRepeat']) ||
              component.getIn(['props', 'isRepeatCol']) ||
              repContainers.includes(component.get('type'))

            if (isRepeat && payload.index !== null && payload.index !== undefined) {
              const rulesPath = ['rules', pageUID, parentID, parentID, String(payload.index)]
              const rules = state.getIn(rulesPath, List())
              if (List.isList(rules) && rules.includes('hide')) {
                if (_.size(payload.onValid) > 0) {
                  dispatch(
                    processButtonAction(
                      fromJS(payload.onValid),
                      'onValid',
                      typeof payload.index === 'number',
                      payload.index,
                      payload.options
                    )
                  )
                }
                return
              }
            } else {
              const rulesPath = ['rules', pageUID, parentID]
              const rules = state.getIn(rulesPath, List())
              if (List.isList(rules) && rules.includes('hide')) {
                if (_.size(payload.onValid) > 0) {
                  dispatch(
                    processButtonAction(
                      fromJS(payload.onValid),
                      'onValid',
                      typeof payload.index === 'number',
                      payload.index,
                      payload.options
                    )
                  )
                }
                return
              }
            }
          }
        }
        _.each(allChildren, (uid) => {
          let path = repeatRoot.includes(elemPath[1]) ? `${elemPath[0]}:${elemPath[1]}:${uid}` : `${elemPath[0]}:${uid}`
          let data = validateFieldWithIndex(path, payload.index, state, dispatch)
          isInvalid = data.firstErrorField
          if (isInvalid) {
            firstElement = uid
            invalidIndex = data.firstErrorFieldIndex
            return false
          }
        })
        if (isInvalid) {
          // if (_.size(payload.onInValid) > 0) {
          //     let opt = getPositionOfElem(payload.options, firstElement)
          //     dispatch(processButtonAction(fromJS(payload.onInValid), 'onInValid', typeof payload.index === 'number', payload.index, opt))
          // }
        } else {
          if (_.size(payload.onValid) > 0) {
            dispatch(
              processButtonAction(
                fromJS(payload.onValid),
                'onValid',
                typeof payload.index === 'number',
                payload.index,
                payload.options
              )
            )
          }
        }
        break
      }
    }

    if (isInvalid) {
      let pageID = state.get('page')
      const autoTabOrder = state.getIn(['project', 'Settings', 'ui', 'autoTabOrder'], false)
      let useAutoTabOrder = false
      if (pageID !== 'project') {
        useAutoTabOrder = autoTabOrder && state.getIn(['pages', pageID, 'Props', 'TabOrder'], 'auto') === 'auto'
      } else {
        useAutoTabOrder = autoTabOrder && state.getIn(['project', 'Settings', 'ui', 'TabOrder'], 'auto') === 'auto'
      }
      const layoutMode = state.getIn(['layoutMode'], 'lg')
      if (!useAutoTabOrder) {
        invalidChild = _.sortBy(invalidChild, (child) => {
          return parseInt(
            state.getIn([...getElementDataRoot(pageID), 'elements', child, 'props', layoutMode, 'tabOrder'], ''),
            10
          )
        })
        firstElement = invalidChild[0] || firstElement
      }
      if (firstElement) {
        const onInvalidAction = fromJS(payload.onInValid)
        let state = getAppState()
        dispatch({
          type: actionTypes.SET_STORE,
          payload: {
            store: state.setIn(
              ['__cache__'],
              state.getIn(['__cache__'], List()).push(
                fromJS({
                  type: 'set_focus',
                  payload: {
                    uid: firstElement,
                    index: invalidIndex,
                    noScrollFix: true,
                    noReveal,
                    action: onInvalidAction,
                  },
                })
              )
            ),
          },
        })
      }
    }
  }

export const processConditionQueCache = (inCache) => (dispatch, getState) => {
  dispatch({
    type: actionTypes.PROJECT_PROCESS_COND_QUE_CACHE,
  })
}

export const isParentRepeat = (state, uid) => {
  let elem = state.hasIn(['project', 'Settings', 'elements', uid])
    ? state.getIn(['project', 'Settings', 'elements', uid], Map())
    : state.getIn(['pages', state.get('page'), 'Props', 'elements', uid], Map())
  if (elem.get('type') === 'RepeatAutoFit') {
    return true
  } else if (elem.get('type') === 'Strip') {
    return state.getIn(['project', 'Settings', 'RepeatedStripsID'], List()).findIndex((id) => id === uid) !== -1
  } else if (elem.get('type') == 'RepeatAutoFit') {
    return true
  } else if (elem.get('type')) {
    return isParentRepeat(state, elem.get('parent'))
  } else {
    return false
  }
}

export const getParentRepeatUID = (state, uid) => {
  let elem = state.hasIn(['project', 'Settings', 'elements', uid])
    ? state.getIn(['project', 'Settings', 'elements', uid], Map())
    : state.getIn(['pages', state.get('page'), 'Props', 'elements', uid], Map())
  if (elem.get('type') === 'RepeatAutoFit') {
    return uid
  } else if (elem.get('type') === 'Strip') {
    return state.getIn(['project', 'Settings', 'RepeatedStripsID'], List()).findIndex((id) => id === uid) !== -1
      ? uid
      : null
  } else if (elem.get('type')) {
    return getParentRepeatUID(state, elem.get('parent'))
  } else {
    return null
  }
}

const findElementParentTab = (pageID, uid, state) => {
  let elem = state.hasIn(['project', 'Settings', 'elements', uid])
    ? state.getIn(['project', 'Settings', 'elements', uid], Map())
    : state.getIn(['pages', state.get('page'), 'Props', 'elements', uid], Map())
  const parentUID = elem.get('parent')
  const parent = state.hasIn(['project', 'Settings', 'elements', parentUID])
    ? state.getIn(['project', 'Settings', 'elements', parentUID], Map())
    : state.getIn(['pages', state.get('page'), 'Props', 'elements', parentUID], Map())
  if (TAB_TYPES.includes(parent.get('type'))) {
    return parentUID
  } else if (parent.get('type')) {
    return findElementParentTab(pageID, parent.get('parent'), state)
  } else {
    return null
  }
}

const findElementParentTabChild = (pageID, uid, state) => {
  let elem = state.hasIn(['project', 'Settings', 'elements', uid])
    ? state.getIn(['project', 'Settings', 'elements', uid], Map())
    : state.getIn(['pages', state.get('page'), 'Props', 'elements', uid], Map())
  const parentUID = elem.get('parent')
  const parent = state.hasIn(['project', 'Settings', 'elements', parentUID])
    ? state.getIn(['project', 'Settings', 'elements', parentUID], Map())
    : state.getIn(['pages', state.get('page'), 'Props', 'elements', parentUID], Map())
  if (TAB_TYPES.includes(parent.get('type'))) {
    return uid
  } else if (parent.get('type')) {
    return findElementParentTab(pageID, parent.get('parent'), state)
  } else {
    return null
  }
}

export const getRestNodes = (nodes, list, inHash = {}) => {
  let hash = {
    ...inHash,
  }
  _.each(nodes, (node) => {
    if (hash[node.id]) {
      return true
    } else {
      hash[node.id] = node
    }
    const nextNodes = []
    const connections = _.filter(list, (val) => val.source === node.id)
    _.each(connections, (conn) => {
      if (hash[conn.id]) {
        return true
      } else {
        hash[conn.id] = conn
      }
      let connected = _.filter(list, (val) => val.id === conn.target)
      nextNodes.push(...connected)
    })
    hash = {
      ...hash,
      ...getRestNodes(nextNodes, list, hash),
    }
  })
  return hash
}

export const getNextNode = (list, id, type, port) => {
  if (
    ![
      'sfAction',
      'condition',
      'sfEvent',
      'msgDlgtoast',
      'msgDlgconfirm',
      'msgDlgslack',
      'msgDlg',
      'validate',
      'docGen',
      'timerInteractivity',
      'formInteractivity',
      'VirusScan',
      'paymentIntegration',
      'libFlow',
      'loop',
      'sendEmail',
      'uac',
      'AddressValidation',
    ].includes(type)
  ) {
    let connections = _.filter(list, (node) => node.source === id)
    return _.reduce(
      connections,
      (r, connection) => {
        let node = _.find(list, (node) => node.id === connection.target) ?? null
        if (node) {
          return [...r, node]
        }
        return r
      },
      []
    )
  } else {
    let connections = _.filter(list, (node) => node.source === id && node.sourceHandle === port)
    return _.reduce(
      connections,
      (r, connection) => {
        let node = _.find(list, (node) => node.id === connection.target) ?? null
        if (node) {
          return [...r, node]
        }
        return r
      },
      []
    )
  }
}

export const processSFItemCancel = (uid, nodeID) => (dispatch, getState) => {
  let state = getState()
  state.getIn(['__cond__cache__', 'nodes-invalid', uid], List()).map((item) => {
    state = state.setIn(['__cache__'], state.getIn(['__cache__'], List()).push(item))
  })
  state = state.deleteIn(['__cond__cache__', 'nodes', uid], List())
  let invalidNodesCache = state
    .getIn(['__cond__cache__', 'nodes-invalid', uid], List())
    .filter((node) => node.getIn(['payload', 'prevNodeID']) !== nodeID)
  if (!List.isList(invalidNodesCache)) {
    invalidNodesCache = List()
  }

  dispatch({
    type: actionTypes.SET_STORE,
    payload: {
      store: state.setIn(['__cond__cache__', 'nodes-invalid', uid], invalidNodesCache),
    },
  })
}

export const processSFItemCancelClear = (uid) => (dispatch, getState) => {
  dispatch({
    type: actionTypes.SET_STORE,
    payload: {
      store: getState().deleteIn(['__cond__cache__', 'nodes-invalid', uid]),
    },
  })
}

const setupWaitNode = (node, list, state, processUID) => {
  if (node.actionType === 'Wait' && !state.hasIn(['__cond__cache__', 'wait', node.id])) {
    let allConnections = _.filter(list, (lNode) => lNode.target == node.id)
    let waitNodes = _.flatMap(allConnections, (conn) => _.filter(list, (lNode) => lNode.id === conn.source))
    waitNodes = _.filter(waitNodes, (node) => node.type === 'sfAction')
    return state.setIn(
      ['__cond__cache__', 'wait', node.id],
      fromJS(
        _.reduce(
          waitNodes,
          (r, vNode) => ({
            ...r,
            [vNode.id]: false,
          }),
          {}
        )
      )
    )
  }
  return state
}

const getHashActiveParams = (state) => {
  let result = {}
  try {
    let params = state.getIn(['project', 'params'], Map()).toJS()
    _.each(params, (row) => {
      result[row.id] = row
    })
    let pageParams = state.getIn(['pages', state.get('page'), 'Props', 'params'], Map()).toJS()
    _.each(pageParams, (row) => {
      result[row.id] = row
    })
  } catch (err) {
    console.error(err.stack)
  }
  return result
}

const processActionFlowNode =
  (
    current,
    list = [],
    index,
    currentRule,
    currentSingleRule,
    isOnlyEffects,
    path,
    oldStatePass,
    oldParamsPass,
    prevNodeID,
    processUID,
    originIndex
  ) =>
  async (dispatch, getAppState) => {
    const start = performance.now();
    let stateSettings = getAppState()

    if (stateSettings.getIn(['project', 'Settings', 'experiment', 'isReleaseEvents'])) {
      await new Promise((resolve) => {
        requestAnimationFrame(resolve)
      })
    }

    let state = getAppState()
    const PT_SINGLE_LOOP = state.getIn(['project', 'Settings', 'experiment', 'isPtSingleLoop'], false)
    let node = list.find((node) => node.id === current)
    const projectType = state.getIn(['project', 'ProjectType'], 'web')
    if (!IS_SERVER && window.location.href.indexOf('__FLOW__') !== -1) {
      console.log('processActionFlowNode >>>', current, { index, node, prevNodeID, list, path })
    }

    let getOriginalIndex = () => {
      if (originIndex !== undefined) {
        return originIndex
      } else {
        return currentRule.index
      }
    }

    if (current && node) {
      node = JSON.parse(JSON.stringify(node))
      const IS_ALLOW_DEBUG_MODE = state.getIn(['IS_ALLOW_DEBUG_MODE'], false)
      if (IS_ALLOW_DEBUG_MODE) {
        console.log(
          'run node >>> ',
          getNodeTypeLabel(node.actionType || node.type) + (node.tag ? ` / ${node.tag}` : '')
        )
      }
      let currentNodeId = node.id
      const restrictKeys = RESTRICT_RUN_NODES[projectType] ?? []
      if (restrictKeys.includes(node.type) || restrictKeys.includes(node.actionType)) {
        console.log(
          'Node type %s(%s) not allowed to run for %s project type.',
          node.type,
          node.actionType || node.type,
          projectType
        )
        return null
      }
      if (node.type === 'input') {
        currentNodeId = node.newId || node.id
        let nextNode = getNextNode(list, node.id, node.type)
        let cacheList = List()
        let startProcessUID = processUID || node.processUID || uuid()
        _.each(nextNode, (node) => {
          state = setupWaitNode(node, list, state, startProcessUID)
          cacheList = cacheList.push(
            fromJS({
              prevNodeID: currentNodeId,
              list,
              current: node.id,
              index,
              currentRule,
              currentSingleRule,
              isOnlyEffects,
              path,
              oldState: oldStatePass,
              oldParams: oldParamsPass,
              processUID: startProcessUID,
              originIndex: getOriginalIndex(),
            })
          )
        })
        cacheList.map((entry) => {
          processRunActionFlowNodeEntry(entry, dispatch)
        })
      } else if (node.type === 'condition') {
        let portalState = getState(state)
        let params = getParams(state)
        const oldState = oldStatePass || state.get('prevRulesState', Map()).toJS()
        const oldParams = oldParamsPass || state.get('prevRulesParams', Map()).toJS()
        const smartVParams = typeof window !== 'undefined' ? _.get(getPreviewDynamicData(), 'svParams', {}) : {}
        let cacheList = List()
        let singleNodeList = Map()
        let repeatRoot = state.getIn(['project', 'Settings', 'RepeatedStripsID'], List()).toJS()
        let isRepeat = false
        let isList = false
        let isTable = false
        let stateKey = null
        let maxRows = 1
        let limitMaxRows = null
        let customIndexes = null
        const conditions = _.get(node, ['data'], [])
        _.each(conditions, (cond, index) => {
          if (!_.get(cond, ['enable'], true)) {
            //we set always false condition, to not run it. need it to not alter node connections list
            _.set(
              node,
              ['data', index, 'conditions'],
              [
                {
                  field: {
                    label: 'Boolean False',
                    value: 'false',
                    group: 'static',
                  },
                  operator: 'equalsnotcase',
                  valueType: 'field',
                  value: {
                    label: 'Boolean True',
                    value: 'true',
                    group: 'static',
                  },
                  bracket: '',
                  post: 'and',
                },
              ]
            )
          }
        })
        if (index === null || index === undefined) {
          _.each(_.get(node, ['data'], []), (group, ruleIndex) => {
            _.each(group?.conditions, function (rule) {
              isTable = isTableRuleKey(_.get(rule, ['field', 'group'], ''))
              if (isTable) {
                stateKey = _.get(rule, ['field', 'value'])
              } else {
                isTable = isTableRuleKey(_.get(rule, ['value', 'group'], ''))
                if (isTable) {
                  stateKey = _.get(rule, ['value', 'value'])
                }
              }
              isList = isListRuleKey(_.get(rule, ['field', 'group'], ''))
              if (isList) {
                stateKey = _.get(rule, ['field', 'value'])
              } else {
                isList = isListRuleKey(_.get(rule, ['value', 'group'], ''))
                if (isList) {
                  isList = _.get(rule, ['value', 'value'])
                }
              }
              let left = String(_.get(rule, ['field', 'value'])).split(':')
              if (repeatRoot.includes(left[1])) {
                isRepeat = true
                stateKey = _.get(rule, ['field', 'value'])
              }
              if (!isRepeat && _.get(rule, ['valueType']) === 'field') {
                let right = String(_.get(rule, ['value', 'value'], '')).split(':')
                if (repeatRoot.includes(right[1])) {
                  isRepeat = true
                  stateKey = _.get(rule, ['value', 'value'], '')
                }
              }
              if (isList || isTable || isRepeat) {
                let key = String(stateKey).split(':')
                try {
                  if (isList && key.length > 2 && _.last(key) !== 'selected') {
                    let isPartialLogic = getElementPropsByUID(state, key[1], ['props', 'isLogicPerPage'])
                    let isEnabledPagination = getElementPropsByUID(state, key[1], [
                      'props',
                      state.get('layoutMode'),
                      'usePagination',
                    ])
                    if (isPartialLogic && isEnabledPagination) {
                      limitMaxRows = ~~getElementPropsByUID(state, key[1], ['props', 'pagination', 'rowsPerPage'], 10)
                      if (_.get(currentRule, ['processMeta', 'isPowerTablePagerProcess'])) {
                        customIndexes = _.get(currentRule, ['processMeta', 'customIndexes'])
                      }
                      let condKey = _.initial(key)
                      condKey.push('isProcessedOnloadRule')
                      const setProcessedCond = (index) => {
                        try {
                          state = state.setIn(['state', ...condKey, index], true)
                        } catch (err) {
                          console.error(err)
                        }
                      }
                      if (!state.getIn(['state', ...condKey])) {
                        state = state.setIn(['state', ...condKey], List())
                      }
                      if (customIndexes) {
                        _.each(customIndexes, setProcessedCond)
                      } else if (limitMaxRows) {
                        let rows = _.get(portalState, key, [])
                        limitMaxRows = _.min([limitMaxRows, rows.length])
                        _.times(limitMaxRows, setProcessedCond)
                      }
                      if (limitMaxRows || customIndexes) {
                        dispatch({
                          type: actionTypes.SET_STORE,
                          payload: {
                            store: state,
                          },
                        })
                      }
                    }
                  }
                } catch (err) {
                  console.error(err)
                }
                let rows = []
                if (isList) {
                  rows = _.get(portalState, key, [])
                } else if (isTable) {
                  rows = _.get(portalState, _.initial(key), [])
                } else if (isRepeat) {
                  rows = _.get(portalState, _.slice(key, 0, 2), [])
                }
                if (isRepeat) {
                  let num = getMappingCount(rows)
                  rows = _.times(num, (n) => n)
                } else {
                  if (!_.isArray(rows)) {
                    rows = [rows]
                  }
                }
                maxRows = _.max([maxRows, rows.length])
              }
            })
          })
        }

        const processRuleRow = (localIndex) => {
          let nodeKey = 'else'
          _.each(_.get(node, ['data'], []), (rule, ruleIndex) => {
            const ruleRunResult = processRuleCondition(
              _.get(rule, ['conditions'], []),
              portalState,
              params,
              smartVParams,
              localIndex,
              {
                ...(isTable ? { tableIndex: localIndex } : {}),
                ...(isList ? { listIndex: localIndex } : {}),
              },
              false,
              oldState,
              oldParams,
              state
            )
            if (ruleRunResult) {
              nodeKey = `then-${ruleIndex}`
              let nextNode = getNextNode(list, node.id, node.type, nodeKey)
              _.each(nextNode, (node) => {
                if (node.actionType === 'sfAction') {
                  let thenList = singleNodeList.get(nodeKey, Map())
                  if (!thenList.get(node.id)) {
                    state = setupWaitNode(node, list, state, processUID)
                    thenList = thenList.set(
                      node.id,
                      fromJS({
                        prevNodeID: currentNodeId,
                        list: list,
                        current: node.id,
                        index: getOriginalIndex(),
                        currentRule,
                        currentSingleRule,
                        isOnlyEffects,
                        path,
                        processUID,
                        originIndex: getOriginalIndex(),
                      })
                    )
                  }
                  singleNodeList = singleNodeList.set(nodeKey, thenList)
                } else {
                  state = setupWaitNode(node, list, state, processUID)
                  cacheList = cacheList.push(
                    fromJS({
                      prevNodeID: currentNodeId,
                      list: list,
                      current: node.id,
                      index: localIndex,
                      currentRule,
                      currentSingleRule,
                      isOnlyEffects,
                      path,
                      processUID,
                      originIndex: getOriginalIndex(),
                    })
                  )
                }
              })
            }
          })
          if (nodeKey === 'else') {
            let nextNode = getNextNode(list, node.id, node.type, nodeKey)
            _.each(nextNode, (node) => {
              if (node.actionType === 'sfAction') {
                let thenList = singleNodeList.get(nodeKey, Map())
                if (!thenList.get(node.id)) {
                  state = setupWaitNode(node, list, state, processUID)
                  thenList = thenList.set(
                    node.id,
                    fromJS({
                      prevNodeID: currentNodeId,
                      list: list,
                      current: node.id,
                      index: getOriginalIndex(),
                      currentRule,
                      currentSingleRule,
                      isOnlyEffects,
                      path,
                      processUID,
                      originIndex: getOriginalIndex(),
                    })
                  )
                }
                singleNodeList = singleNodeList.set(nodeKey, thenList)
              } else {
                state = setupWaitNode(node, list, state, processUID)
                cacheList = cacheList.push(
                  fromJS({
                    prevNodeID: currentNodeId,
                    list: list,
                    current: node.id,
                    index: localIndex,
                    currentRule,
                    currentSingleRule,
                    isOnlyEffects,
                    path,
                    processUID,
                    originIndex: getOriginalIndex(),
                  })
                )
              }
            })
          }
        }
        if (maxRows > 1) {
          if (customIndexes) {
            _.each(customIndexes, (localIndex) => {
              processRuleRow(localIndex)
            })
          } else {
            if (limitMaxRows && limitMaxRows < maxRows) {
              maxRows = limitMaxRows
            }
            _.times(maxRows, function (localIndex) {
              processRuleRow(localIndex)
            })
          }
          singleNodeList.map((nodes) => {
            nodes.map((rule) => {
              cacheList = cacheList.push(rule)
            })
          })
        } else {
          if (maxRows === 1 && (index === null || index === undefined) && (isList || isRepeat || isTable)) {
            processRuleRow(0)
          } else {
            processRuleRow(index)
          }
          singleNodeList.map((nodes) => {
            nodes.map((rule) => {
              cacheList = cacheList.push(rule)
            })
          })
        }
        const getStateFn = () => state
        const dispatchFn = (payload) => {
          if (payload.type === actionTypes.SET_STORE) {
            state = payload.payload.store
          } else if (typeof payload === 'function') {
            payload(dispatchFn, getStateFn)
          } else {
            console.log('something different')
          }
        }
        cacheList.map((entry) => {
          if (PT_SINGLE_LOOP) {
            const payload = entry.toJS()
            const list = payload.list || []
            const current = payload.current
            const index = payload.index
            const currentRule = payload.currentRule
            const currentSingleRule = payload.currentSingleRule
            const isOnlyEffects = payload.isOnlyEffects
            const path = payload.path
            const oldState = payload.oldState
            const oldParams = payload.oldParams
            const prevNodeID = payload.prevNodeID
            const processUID = payload.processUID || uuid()
            let originIndex = payload.originIndex
            if (originIndex === undefined) {
              originIndex = index
            }
            processActionFlowNode(
              current,
              list,
              index,
              currentRule,
              currentSingleRule,
              isOnlyEffects,
              path,
              oldState,
              oldParams,
              prevNodeID,
              processUID,
              originIndex
            )(dispatchFn, getStateFn)
          } else {
            processRunActionFlowNodeEntry(entry, dispatch)
          }
        })
        if (PT_SINGLE_LOOP) {
          dispatch({
            type: actionTypes.SET_STORE,
            payload: {
              store: state,
            },
          })
        }
      } else if (node.actionType === 'VirusScan') {
        let result = await processVirusScan(node, getAppState, dispatch)
        let state = getAppState()

        let cacheList = state.getIn(['__cache__'], List())
        let nextNode
        if (result.status) {
          nextNode = getNextNode(list, node.id, node.actionType, null)
        } else {
          nextNode = getNextNode(list, node.id, node.actionType, 'invalid')
        }

        let startProcessUID = processUID || node.processUID || uuid()
        _.each(nextNode, (node) => {
          state = setupWaitNode(node, list, state, startProcessUID)
          cacheList = cacheList.push(
            fromJS({
              type: 'FlowActionStep',
              payload: {
                prevNodeID: currentNodeId,
                list: list,
                current: node.id,
                index,
                currentRule,
                currentSingleRule,
                isOnlyEffects,
                path,
                oldState: oldStatePass,
                oldParams: oldParamsPass,
                processUID: startProcessUID,
                originIndex: getOriginalIndex(),
              },
            })
          )
        })
        dispatch({
          type: actionTypes.SET_STORE,
          payload: {
            store: state.setIn(['__cache__'], cacheList),
          },
        })
      } else if (node.actionType === 'AddressValidation') {
        let state = getAppState()
        let cacheList = state.getIn(['__cache__'], List())

        const addressSource = _.get(node, ['data', 'AddressValidation', '0', 'addressSource'])
        const errorMessageTarget = _.get(node, ['data', 'AddressValidation', '0', 'errorMessageTarget'])
        const responseJSONTarget = _.get(node, ['data', 'AddressValidation', '0', 'responseJSONTarget'])
        const suggestedAddressTarget = _.get(node, ['data', 'AddressValidation', '0', 'suggestedAddressTarget'])
        const showErrorMessage = _.get(node, ['data', 'AddressValidation', '0', 'showErrorMessage'])
        const showSuggestedAddress = _.get(node, ['data', 'AddressValidation', '0', 'showSuggestedAddress'])

        const addressValue = getValueByUID(state, addressSource)

        const duringRunNodes = getNextNode(list, node.id, node.actionType, null)
        const onErrorNodes = getNextNode(list, node.id, node.actionType, 'fail')
        const onValidNodes = getNextNode(list, node.id, node.actionType, 'onValid')
        const onInvalidNodes = getNextNode(list, node.id, node.actionType, 'onInValid')

        const startProcessUID = processUID || node.processUID || uuid()

        _.each(duringRunNodes, (node) => {
          state = setupWaitNode(node, list, state, startProcessUID)
          cacheList = cacheList.push(
            fromJS({
              type: 'FlowActionStep',
              payload: {
                prevNodeID: currentNodeId,
                list: list,
                current: node.id,
                index,
                currentRule,
                currentSingleRule,
                isOnlyEffects,
                path,
                oldState: oldStatePass,
                oldParams: oldParamsPass,
                processUID: startProcessUID,
                originIndex: getOriginalIndex(),
              },
            })
          )
        })
        dispatch({
          type: actionTypes.SET_STORE,
          payload: {
            store: state.setIn(['__cache__'], cacheList),
          },
        })

        const { data, error } = await processAddressValidation(addressValue, dispatch)

        state = getAppState()

        if (errorMessageTarget && data.errorMessage) {
          state = setValueByUID(state, errorMessageTarget, data.errorMessage)
        }
        if (responseJSONTarget && data.responseJSON) {
          state = setValueByUID(state, responseJSONTarget, data.responseJSON)
        }
        if (suggestedAddressTarget && data.validationDetails?.suggestedAddress) {
          state = setValueByUID(state, suggestedAddressTarget, data.validationDetails.suggestedAddress)
        }
        dispatch({
          type: actionTypes.SET_STORE,
          payload: {
            store: state,
          },
        })

        let nextNodes = data?.isValid ? onValidNodes : onInvalidNodes
        if (error) {
          nextNodes = onErrorNodes
        }

        cacheList = state.getIn(['__cache__'], List())

        _.each(nextNodes, (node) => {
          state = setupWaitNode(node, list, state, startProcessUID)
          cacheList = cacheList.push(
            fromJS({
              type: 'FlowActionStep',
              payload: {
                prevNodeID: currentNodeId,
                list: list,
                current: node.id,
                index,
                currentRule,
                currentSingleRule,
                isOnlyEffects,
                path,
                oldState: oldStatePass,
                oldParams: oldParamsPass,
                processUID: startProcessUID,
                originIndex: getOriginalIndex(),
              },
            })
          )
        })
        dispatch({
          type: actionTypes.SET_STORE,
          payload: {
            store: state.setIn(['__cache__'], cacheList),
          },
        })

        if (showErrorMessage && !data?.isValid && !data?.errorMessage && data?.validationDetails) {
          dispatch(
            openAddressValidationErrorMessageDialog({
              showSuggestedAddress,
              validationDetails: data.validationDetails,
            })
          )
        }
      } else if (node.actionType === 'Submissions') {
        let result = await processSubmissionFlowNode(node, getAppState, dispatch)
        let state = getAppState()
        const RepeatedStripsID = state.getIn(['project', 'Settings', 'RepeatedStripsID'], List()).toJS()
        if (result.status) {
          let sett = _.get(node, ['data', 'Submissions', 0])

          const filterConstVar = (param) => {
            if (
              (isImmutable(param) && (param.get('isConstant') || param.get('isReadOnly'))) ||
              ['numeric', 'customJS', 'string', 'system'].includes(param.get('paramType'))
            ) {
              return true
            }
            return false
          }

          const constVariables = state
            .getIn(['project', 'params'], List())
            .filter(filterConstVar)
            .map((param) => param.get('id'))
            .toJS()
          const constPageVariables = state
            .getIn(['pages', state.get('page', null), 'Props', 'params'], List())
            .filter(filterConstVar)
            .map((param) => param.get('id'))
            .toJS()
          const allPageVariables = state
            .getIn(['pages', state.get('page', null), 'Props', 'params'], List())
            .map((param) => param.get('id'))
            .toJS()

          if (sett.action === 'load') {
            const projectId = result?.meta?.projectId
            const id = result?.meta?.id
            state = state.setIn(['_submission_cache_', projectId, id], fromJS(result.data))
          }
          let changes = []
          const setValueToState = (val, key) => {
            try {
              let elPath = String(key).split(':')
              if (RepeatedStripsID.includes(elPath[1])) {
                let elem = state.hasIn(['project', 'Settings', 'elements', elPath[2], 'type'])
                  ? state.getIn(['project', 'Settings', 'elements', elPath[2]], Map())
                  : state.getIn(['pages', state.get('page'), 'Props', 'elements', elPath[2]], Map())
                if (['radio', 'lookup', 'daterange'].includes(elem.getIn(['props', 'control']))) {
                  let path = _.initial(elPath)
                  let prop = _.last(elPath)
                  changes.push(elPath)
                  _.each(val, (value, n) => {
                    state = state.setIn(['state', ...path, String(n), prop], fromJS(value))
                  })
                } else {
                  let path = String(key).split(':')
                  changes.push(path)
                  if (_.last(path) === 'ft_fileupload') {
                    let filesList = []
                    _.each(val, (rowValue, rowIndex) => {
                      filesList.push({
                        ft_fileupload: rowValue,
                      })
                    })
                    state = state.setIn(['state', ..._.initial(path)], fromJS(filesList))
                  } else if (_.last(path) === 'signature_base64') {
                    state = state.setIn(['state', ..._.initial(path)], fromJS(val))
                  } else {
                    state = state.setIn(['state', ...path], fromJS(val))
                  }
                }
              } else {
                if (String(key).indexOf(':') > -1) {
                  let path = String(key).split(':')
                  changes.push(path)
                  if (_.last(path) === 'ft_fileupload') {
                    console.log('no rep >>>', path, val)
                    state = state.setIn(['state', ...path], fromJS(val))
                  } else if (_.last(path) === 'signature_base64') {
                    state = state.setIn(['state', ..._.initial(path)], fromJS(val))
                  } else {
                    //path.length = 2 => usual for page vars. + last item = var id
                    if (path.length === 2 && allPageVariables.includes(_.last(path))) {
                      //only if editable
                      if (!constPageVariables.includes(_.last(path))) {
                        state = state.setIn(['state', ...path], fromJS(val))
                      }
                    } else {
                      state = state.setIn(['state', ...path], fromJS(val))
                    }
                  }
                } else {
                  changes.push([key])
                  if (!constVariables.includes(key)) {
                    state = state.setIn(['params', key], val)
                  }
                }
              }
            } catch (error) {
              console.error(error)
            }
          }
          if (sett.action == 'store') {
            if (sett.storeSubmissionID && sett.storeSubmissionID.value && result.Id) {
              setValueToState(result.Id, sett.storeSubmissionID.value)
            }
            if (sett?.storeSubmissionJSON?.value && result.Id) {
              setValueToState(JSON.stringify(result.data), sett?.storeSubmissionJSON?.value)
            }
          } else if (sett.action == 'load') {
            if (result.data) {
              _.each(result.data, setValueToState)
            }
          }
          if (changes.length > 0) {
            state = doProcessRules(state, false, [], [], changes, false, false, index)
          }
        }
        let cacheList = state.getIn(['__cache__'], List())
        let nextNode = getNextNode(list, node.id, node.type)
        let startProcessUID = processUID || node.processUID || uuid()
        _.each(nextNode, (node) => {
          state = setupWaitNode(node, list, state, startProcessUID)
          cacheList = cacheList.push(
            fromJS({
              type: 'FlowActionStep',
              payload: {
                prevNodeID: currentNodeId,
                list: list,
                current: node.id,
                index,
                currentRule,
                currentSingleRule,
                isOnlyEffects,
                path,
                oldState: oldStatePass,
                oldParams: oldParamsPass,
                processUID: startProcessUID,
                originIndex: getOriginalIndex(),
              },
            })
          )
        })
        dispatch({
          type: actionTypes.SET_STORE,
          payload: {
            store: state.setIn(['__cache__'], cacheList),
          },
        })
      } else if (node.actionType === 'docGen' && _.get(node, ['data', 'docGen', 0, 'action']) === 'download') {
        let portalState = getState(state)
        let params = getParams(state)
        const oldState = oldStatePass || state.get('prevRulesState', Map()).toJS()
        const oldParams = oldParamsPass || state.get('prevRulesParams', Map()).toJS()

        let nextNode1 = getNextNode(list, node.id, node.actionType, null)
        let nextNode2 = getNextNode(list, node.id, node.actionType)
        let nextNode = nextNode1.concat(nextNode2)
        let finishNode = getNextNode(list, node.id, node.actionType, 'finish')

        let startProcessUID = processUID || node.processUID || uuid()
        const processRuleTargets = getProcessTargetsFunction(
          state,
          fromJS(currentRule),
          isOnlyEffects,
          portalState,
          params,
          false,
          false,
          undefined,
          undefined,
          oldState,
          oldParams,
          path,
          getOriginalIndex()
        )
        if (node.data && finishNode && _.size(finishNode)) {
          let finishCacheList = List()
          _.each(finishNode, (node) => {
            finishCacheList = finishCacheList.push(
              fromJS({
                type: 'FlowActionStep',
                payload: {
                  prevNodeID: currentNodeId,
                  list: list,
                  current: node.id,
                  index,
                  currentRule,
                  currentSingleRule,
                  isOnlyEffects,
                  path,
                  oldState: null,
                  oldParams: null,
                  processUID: startProcessUID,
                  originIndex: getOriginalIndex(),
                },
              })
            )
          })
          _.set(node, ['data', 'docGen', 0, 'nextNode'], finishCacheList)
        }
        let actions = fromJS(node.data)
        const result = processRuleTargets(
          index,
          typeof index !== 'undefined' && index !== null,
          false,
          fromJS(currentSingleRule),
          actions
        )
        state = result.retState
        let cacheList = state.getIn(['__cache__'], List())
        _.each(nextNode, (node) => {
          cacheList = cacheList.push(
            fromJS({
              type: 'FlowActionStep',
              payload: {
                prevNodeID: currentNodeId,
                list: list,
                current: node.id,
                index,
                currentRule,
                currentSingleRule,
                isOnlyEffects,
                path,
                oldState: oldStatePass,
                oldParams: oldParamsPass,
                processUID: startProcessUID,
                originIndex: getOriginalIndex(),
              },
            })
          )
        })
        state = state.setIn(['__cache__'], cacheList)

        dispatch({
          type: actionTypes.SET_STORE,
          payload: {
            store: state,
          },
        })
      } else if (node.type === 'sfAction') {
        processUID = current
        let sfActionIndex = currentRule.index
        if (originIndex !== undefined) {
          sfActionIndex = originIndex
        } else if (sfActionIndex === undefined) {
          sfActionIndex = index
        }
        const isOnload = false
        let loadedNode = getNextNode(list, node.id, node.type, 'loaded')
        let duringNode = getNextNode(list, node.id, node.type, 'during')
        let invalidNode = getNextNode(list, node.id, node.type, 'invalid')
        let items = node.data.sfAction?.[0]?.items || []

        let mappingList = state.getIn(['project', 'Settings', 'sfMapping'], Map()).toJS()
        let pushMappingList = state.getIn(['project', 'Settings', 'pushToSalesforce'], List())
        pushMappingList.map((row) => {
          if (row && isImmutable(row)) {
            mappingList[row.get('uid')] = row.toJS()
          }
        })
        pushMappingList = pushMappingList.toJS()
        items = _.filter(items, function (row) {
          if (row && row.value && mappingList[row.value]) {
            if (mappingList[row.value].type != 'custom' && !row.isPush) {
              return false
            }
            if (!isOnload || mappingList[row.value].runAlways) {
              return true
            }

            if (state.getIn(['historySF', row.value])) {
              return false
            } else {
              if (!isOnload && !mappingList[row.value].runAlways) {
                state = state.setIn(['historySF', row.value], true)
              }
              return true
            }
          }
        })
        let hashRules = {}
        let firstErrorField = null,
          firstErrorFieldIndex = sfActionIndex
        const layoutMode = state.get('layoutMode', 'lg')
        const allFields = _.reduce(items, (r, row) => _.concat([], r, mappingList?.[row?.value]?.fields || []), [])
        const fields = _.sortBy(allFields, (field) => {
          let fieldUID
          if (field && typeof field == 'object') {
            fieldUID = field.value
          } else {
            fieldUID = String(field).split('##')[1]
          }
          if (fieldUID && String(fieldUID).indexOf('ft_search') === -1) {
            fieldUID = String(fieldUID).split(':')
            let pageUID = fieldUID[0]
            let elemUID = fieldUID[1]
            let repeatRoot = state.getIn(['project', 'Settings', 'RepeatedStripsID'], List()).toJS()
            if (repeatRoot.indexOf(fieldUID[1]) > -1) {
              elemUID = fieldUID[2]
            }
            let page = state.getIn(['project', 'Pages'], List()).find((page) => page.get('uid') === pageUID)
            if (page) {
              let component = state.getIn(['pages', page.get('_id'), 'Props', 'elements', elemUID], Map())
              return parseInt(component.getIn(['props', layoutMode, 'tabOrder'], '0'), 10) || 0
            }
          }
          return 0
        })
        if (!_.get(node.data, ['sfAction', 0, 'ignoreValidations'])) {
          _.each(fields, function (field) {
            let fieldUID
            if (field && typeof field == 'object') {
              fieldUID = field.value
            } else {
              fieldUID = String(field).split('##')[1]
            }

            if (fieldUID && String(fieldUID).indexOf('ft_search') === -1) {
              fieldUID = String(fieldUID).split(':')
              let pageUID = fieldUID[0]
              let elemUID = fieldUID[1]
              let repeatRoot = state.getIn(['project', 'Settings', 'RepeatedStripsID'], List()).toJS()
              if (repeatRoot.indexOf(fieldUID[1]) > -1) {
                elemUID = fieldUID[2]
              }
              let page = state.getIn(['project', 'Pages'], List()).find((page) => page.get('uid') === pageUID)
              if (page) {
                let component = state.getIn(['pages', page.get('_id'), 'Props', 'elements', elemUID], Map())
                const parentStatus = getParentRulesStatus(
                  component.get('parent'),
                  page.get('_id'),
                  pageUID,
                  sfActionIndex,
                  elemUID,
                  fieldUID[1],
                  state
                )
                let localPath = ['formFieldStatuses', pageUID, elemUID]
                let rulesPath = ['rules', pageUID, elemUID]
                if (repeatRoot.indexOf(fieldUID[1]) !== -1) {
                  let maxItems = 0
                  let repData = state.getIn(['state', pageUID, fieldUID[1]], Map())
                  if (repData && isImmutable(repData)) {
                    repData = repData.toJS()
                    maxItems = getMappingCount(repData)
                    _.each(repData, function (row) {
                      if (row && _.isArray(row)) {
                        if (maxItems < row.length) {
                          maxItems = row.length
                        }
                      }
                    })
                  }
                  function processCheckIndex(localIndex) {
                    let localPath2 = ['formFieldStatuses', pageUID, fieldUID[1], fieldUID[2], String(localIndex)]
                    let rulesPath = ['rules', pageUID, fieldUID[1], fieldUID[2], localIndex]
                    try {
                      const parentElementStatus = getParentRulesStatus(
                        component.get('parent'),
                        page.get('_id'),
                        pageUID,
                        localIndex,
                        elemUID,
                        fieldUID[1],
                        state
                      )
                      if (parentElementStatus.includes('hide')) {
                        return false
                      }
                    } catch (err) {
                      console.error(err)
                    }
                    let dataPath = [fieldUID[2]]
                    if (fieldUID.length == 4 && component.getIn(['props', 'control']) !== 'signature') {
                      dataPath.push(fieldUID[3])
                    }
                    dataPath.push(localIndex)
                    const rulesStatus = state.getIn(rulesPath, List())
                    let rules = List.isList(rulesStatus) ? rulesStatus.toJS() : []
                    try {
                      let parentRuleRulesPath = ['rules', pageUID, fieldUID[1], fieldUID[1], localIndex]
                      const parentRuleStatus = state.getIn(parentRuleRulesPath, List())
                      let parentRules = List.isList(parentRuleStatus) ? parentRuleStatus.toJS() : []
                      if (parentRules.includes('hide')) {
                        return false
                      }
                    } catch (err) {
                      console.error(err)
                    }
                    let value
                    if (rules.includes('notMandatory')) {
                      component = component.setIn(['props', state.get('layoutMode', 'lg'), 'mandatory'], false)
                    }
                    if (component.get('type') === 'FormField' && component.getIn(['props', 'control']) === 'radio') {
                      value = _.get(repData, [fieldUID[2], localIndex, 'ftListValue'])
                    } else if (
                      component.get('type') === 'FormField' &&
                      component.getIn(['props', 'control']) === 'daterange'
                    ) {
                      value = _.get(repData, [fieldUID[2], localIndex, 'endDate'])
                    } else if (
                      component.get('type') === 'FormField' &&
                      component.getIn(['props', 'control']) === 'lookup'
                    ) {
                      value = _.get(repData, [fieldUID[2], localIndex])
                    } else {
                      value = _.get(repData, dataPath)
                    }
                    let ruleStatus = processValueRules(
                      fieldUID[2],
                      component,
                      undefined,
                      localIndex,
                      value,
                      rulesPath
                    )(null, () => state)
                    //@note error for visible elements
                    if (ruleStatus.status && rules.indexOf('hide') === -1 && parentStatus.indexOf('hide') === -1) {
                      if (!firstErrorField) {
                        firstErrorField = field
                        firstErrorFieldIndex = localIndex
                      }
                      if (!currentRule.isNotSetValueRule) {
                        state = state.setIn(localPath2, fromJS(ruleStatus.ruleStatus))
                      }
                      hashRules[elemUID] = ruleStatus.error
                    }
                  }
                  if (sfActionIndex === undefined || sfActionIndex === null) {
                    _.times(maxItems, processCheckIndex)
                  } else if (sfActionIndex !== undefined && sfActionIndex !== null) {
                    processCheckIndex(sfActionIndex)
                  }
                } else {
                  const rulesStatus = state.getIn(rulesPath, List())
                  let rules = List.isList(rulesStatus) ? rulesStatus.toJS() : []
                  let ruleStatus
                  let value = undefined
                  let localIndex = undefined
                  let localPath2 = [...localPath]
                  if (
                    sfActionIndex !== undefined &&
                    sfActionIndex !== null &&
                    (repeatRoot.includes(fieldUID[1]) || component.get('type') === 'PowerTable')
                  ) {
                    localIndex = sfActionIndex
                    rulesPath = ['rules', pageUID, fieldUID[1], fieldUID[2], sfActionIndex]
                    let dataPath = [fieldUID[2]]
                    if (fieldUID.length == 4 && component.getIn(['props', 'control']) !== 'signature') {
                      dataPath.push(fieldUID[3])
                    }
                    dataPath.push(sfActionIndex)
                    let repData = state.getIn(['state', pageUID, fieldUID[1]], Map())
                    if (repData && isImmutable(repData)) {
                      repData = repData.toJS()
                    } else {
                      repData = {}
                    }
                    if (
                      component.get('type') === 'FormField' &&
                      component.getIn(['props', 'control']) === 'daterange'
                    ) {
                      value = _.get(repData, [fieldUID[2], localIndex, 'endDate'])
                    } else if (
                      component.get('type') === 'FormField' &&
                      component.getIn(['props', 'control']) === 'lookup'
                    ) {
                      value = _.get(repData, [fieldUID[2], localIndex])
                    } else {
                      value = _.get(repData, dataPath)
                    }
                    localPath2 = ['formFieldStatuses', pageUID, fieldUID[1], fieldUID[2], String(localIndex)]
                  }
                  ruleStatus = processValueRules(
                    elemUID,
                    component,
                    undefined,
                    localIndex,
                    value,
                    rulesPath,
                    'sfValidation',
                    state.getIn(localPath2)
                  )(null, () => state)
                  //@note error for visible elements
                  if (ruleStatus.status && rules.indexOf('hide') === -1 && parentStatus.indexOf('hide') === -1) {
                    if (!firstErrorField) {
                      firstErrorField = field
                    }
                    if (!currentRule.isNotSetValueRule) {
                      state = state.setIn(localPath2, fromJS(ruleStatus.ruleStatus))
                    }
                    hashRules[elemUID] = ruleStatus.error
                  }
                }
              } else {
                try {
                  const projectElement = state.getIn(['project', 'Settings', 'elements', elemUID])
                  let rulesPath = ['rules', pageUID, elemUID]
                  let localPath = ['formFieldStatuses', pageUID, elemUID]
                  let ruleStatus = processValueRules(
                    elemUID,
                    projectElement,
                    undefined,
                    undefined,
                    undefined,
                    rulesPath,
                    'sfValidation',
                    state.getIn(localPath)
                  )(null, () => state)
                  if (ruleStatus.status) {
                    if (!firstErrorField) {
                      firstErrorField = field
                    }
                    if (!currentRule.isNotSetValueRule) {
                      state = state.setIn(localPath, fromJS(ruleStatus.ruleStatus))
                    }
                    hashRules[elemUID] = ruleStatus.error
                  }
                } catch (e) {
                  console.error(e)
                }
              }
            }
          })
        }
        if (firstErrorField && !_.get(node.data, ['sfAction', 0, 'ignoreValidations'])) {
          if (invalidNode.length > 0) {
            _.each(invalidNode, (node) => {
              state = setupWaitNode(node, list, state, processUID)
              state = state.setIn(
                ['__cache__'],
                state.getIn(['__cache__'], List()).push(
                  fromJS({
                    type: 'FlowActionStep',
                    payload: {
                      list: list,
                      current: node.id,
                      index: sfActionIndex,
                      currentRule,
                      currentSingleRule,
                      isOnlyEffects,
                      path,
                      processUID,
                    },
                  })
                )
              )
            })
          }
          // state = state.setIn(
          //     ['__cache__'],
          //     state.getIn(['__cache__'], List())
          //         .push(fromJS({
          //             type: 'FocusErrorField',
          //             payload: {
          //                 field: firstErrorField,
          //                 index: firstErrorFieldIndex
          //             }
          //         }))
          // );
        }
        if (
          state.get('IS_ALLOW_DEBUG_MODE') &&
          state.getIn(['project', 'Settings', 'debugModeSettings', 'skipValidations'])
        ) {
          hashRules = {}
        }
        if (Object.keys(hashRules).length && !_.get(node.data, ['sfAction', 0, 'ignoreValidations'])) {
          if (invalidNode.length === 0) {
            state = state
              .setIn(['widgets', 'isShowSFMessage'], true)
              .setIn(['widgets', 'firstErrorFieldIndex'], firstErrorFieldIndex)
              .setIn(['widgets', 'sfMessages'], fromJS(hashRules))
              .setIn(['widgets', 'clientY', 'sfMessages'], currentRule?.options?.clientY ?? window.ftLastClientY)
          }
        } else if (items.length) {
          state = setVisibilityOfSFMappingElem(mappingList, items, true, null, state)
          _.each(duringNode, (node) => {
            state = setupWaitNode(node, list, state, processUID)
            state = state.setIn(
              ['__cache__'],
              state.getIn(['__cache__'], List()).push(
                fromJS({
                  type: 'FlowActionStep',
                  payload: {
                    prevNodeID: currentNodeId,
                    list: list,
                    current: node.id,
                    index: sfActionIndex,
                    currentRule,
                    currentSingleRule,
                    isOnlyEffects,
                    path,
                    processUID,
                    originIndex: getOriginalIndex(),
                  },
                })
              )
            )
          })
          state = processActionsSFItem(mappingList, items, false, 'duringIntegration', null, state)
          state = processActionsSFItem(pushMappingList, items, false, 'duringIntegration', null, state)
          let ruleUID
          if (currentSingleRule) {
            // ruleUID = currentSingleRule.get('uid');
          }
          //nodes

          let localElemUID
          try {
            if (_.get(currentRule, ['action'])) {
              _.each(_.get(currentRule, ['action']), function (act) {
                if (!localElemUID && _.get(act, ['actions', 'actionFlow', '0', 'elemUID'])) {
                  localElemUID = _.get(act, ['actions', 'actionFlow', '0', 'elemUID'])
                }
              })
            }
          } catch (err) {
            console.error(err)
          }
          if (!localElemUID) {
            localElemUID = currentRule.uid
          }

          let isCaptchaVerification = node.data.sfAction?.[0]?.isVerifyCaptcha ?? false
          let isForceCaptcha = false
          _.each(items, (row) => {
            if (row && row.value && mappingList[row.value] && mappingList[row.value].isVerifyCaptcha) {
              isCaptchaVerification = true
              isForceCaptcha = true
            }
          })
          const payload = {
            isNode: true,
            node: loadedNode,
            ruleUID: ruleUID,
            nodeId: node.id,
            actionRuleId: _.get(currentSingleRule, 'uid'),
            processUID,
            processIndex: null,
            elemUID: localElemUID,
            items: items,
            runInBackground: node.data.sfAction?.[0]?.runInBackground ?? false,
            isVerifyCaptcha: isCaptchaVerification,
            isForceCaptcha: isForceCaptcha,
            customOrder: node.data.sfAction?.[0]?.customOrder ?? false,
            isParallelLoad: node.data.sfAction?.[0]?.isParallelLoad ?? false,
            path,
            index: sfActionIndex,
            options: currentRule.options || {},
            ignoreValidations: _.get(node.data, ['sfAction', 0, 'ignoreValidations']),
          }
          if (sfActionIndex !== null && typeof sfActionIndex !== 'undefined') {
            payload.path = false
            payload.widgetPath = [sfActionIndex]
          }
          _.each(loadedNode, (node) => {
            state = setupWaitNode(node, list, state, processUID)
            let nodeCallItem = fromJS({
              type: 'FlowActionStep',
              payload: {
                prevNodeID: currentNodeId,
                list: list,
                current: node.id,
                index: sfActionIndex,
                currentRule,
                currentSingleRule,
                isOnlyEffects,
                path,
                processUID,
                originIndex: getOriginalIndex(),
              },
            })
            let nodesList = state.getIn(['__cond__cache__', 'nodes', processUID], List())
            if (!List.isList(nodesList)) {
              nodesList = List()
            }
            state = state.setIn(['__cond__cache__', 'nodes', processUID], nodesList.push(nodeCallItem))
          })
          if (_.size(loadedNode) === 0) {
            state = state.deleteIn(['__cond__cache__', 'nodes', processUID])
          }
          state = state.deleteIn(['__cond__cache__', 'nodes-invalid', processUID])
          _.each(invalidNode, (node) => {
            let nodeCallItem = fromJS({
              type: 'FlowActionStep',
              payload: {
                prevNodeID: currentNodeId,
                list: list,
                current: node.id,
                index: sfActionIndex,
                currentRule,
                currentSingleRule,
                isOnlyEffects,
                path,
                processUID,
                originIndex: getOriginalIndex(),
              },
            })
            state = state.setIn(
              ['__cond__cache__', 'nodes-invalid', processUID],
              state.getIn(['__cond__cache__', 'nodes-invalid', processUID], List()).push(nodeCallItem)
            )
          })
          state = state
            .setIn(['sfActions', 'needCall'], true)
            .setIn(['sfActions', 'params'], state.getIn(['sfActions', 'params'], List()).push(fromJS(payload)))
        }
        dispatch({
          type: actionTypes.SET_STORE,
          payload: {
            store: state,
          },
        })
        ///call sf to continue
      } else if (['loop', 'loopContinue', 'loopBreak'].includes(node.actionType)) {
        const isContinue = node.actionType === 'loopContinue'
        const isBreak = node.actionType === 'loopBreak'
        if (window.__FT__APP__STATE?.__cache__?.[processUID]) {
          //restore run params
          index = currentRule.index = window.__FT__APP__STATE.__cache__[processUID].loopIndex
          currentRule.loopIndex = window.__FT__APP__STATE.__cache__[processUID].loopIndex
          currentRule.loopCaller = window.__FT__APP__STATE.__cache__[processUID].loopCaller
          originIndex = currentRule.loopOriginIndex = window.__FT__APP__STATE.__cache__[processUID].loopOriginIndex
        }
        if (isContinue) {
          node = _.find(list, (val) => val.id === currentRule.loopCaller)
        } else if (isBreak) {
          node = _.find(list, (val) => val.id === currentRule.loopCaller)
        }
        if (!node) {
          console.error('No loop node found, Exiting.')
          return null
        }
        state = getAppState()
        let cacheList = state.getIn(['__cache__'], List())
        const target = _.get(node, 'data.loop[0].target.value')
        const targetType = _.get(node, 'data.loop[0].target.type')
        const portalState = getState(state)
        const params = getParams(state)
        const appState = _.assign({}, params, portalState)
        let paramValues = []
        let repeatTimes = -1
        if (['Strip', 'PowerTable', 'InteractiveImage'].includes(targetType)) {
          const stripData = _.get(appState, String(target).split(':').join('.'), {})
          repeatTimes = getMappingCount(stripData)
        } else if (targetType === 'FormField') {
          const ffData = _.get(appState, String(target).split(':').join('.'), {})
          let num = parseInt(ffData, 10)
          if (Number.isNaN(num)) {
            num = 0
          }
          repeatTimes = num
        } else if (targetType === 'param') {
          const value = _.get(appState, String(target).split(':').join('.'), '')
          let delimiter = _.get(node, 'data.loop[0].delimiter.value', ';')
          let delimiterType = _.get(node, ['data', 'loop', 0, 'delimiter', 'type'], 'static')
          if (delimiterType === 'static') {
            delimiter = _.get(node, 'data.loop[0].delimiter.value', ';')
          } else if (delimiterType === 'field') {
            const el = String(_.get(node, 'data.loop[0].delimiter.el', '')).split(':')
            let repeatRoot = state.getIn(['project', 'Settings', 'RepeatedStripsID'], List()).toJS()
            if (repeatRoot.includes(el[1])) {
              delimiter = _.get(appState, [...el, currentRule.loopOriginIndex], '')
            } else {
              delimiter = _.get(appState, el, '')
            }
          }
          paramValues = String(value).split(delimiter)
          if (/^[0-9]+$/.test(value) && !delimiter) {
            let target = parseInt(value, 10)
            if (Number.isNaN(target)) {
              target = 0
            } else if (target > 1000) {
              target = 1000
            }
            paramValues = _.times(target, (n) => n + 1)
          }
          repeatTimes = paramValues.length
        }
        if (isBreak) {
          currentRule.loopIndex = repeatTimes
        }
        if (
          repeatTimes > 0 &&
          ((Number.isInteger(currentRule.loopIndex) && currentRule.loopIndex < repeatTimes - 1) ||
            currentRule.loopIndex === undefined)
        ) {
          if (currentRule.loopIndex < repeatTimes - 1) {
            currentRule.loopIndex += 1
          } else if (currentRule.loopIndex === undefined) {
            currentRule.loopIndex = 0
          }
          currentRule.index = currentRule.loopIndex
          currentRule.loopCaller = node.id
          currentRule.loopOriginIndex = originIndex
          originIndex = currentRule.index
          window.__FT__APP__STATE = window.__FT__APP__STATE || {}
          window.__FT__APP__STATE.__cache__ = window.__FT__APP__STATE.__cache__ || {}
          window.__FT__APP__STATE.__cache__[processUID] = window.__FT__APP__STATE.__cache__[processUID] || {}
          window.__FT__APP__STATE.__cache__[processUID].loopIndex = currentRule.loopIndex
          window.__FT__APP__STATE.__cache__[processUID].loopCaller = currentRule.loopCaller
          window.__FT__APP__STATE.__cache__[processUID].loopOriginIndex = currentRule.loopOriginIndex
          if (targetType === 'param' || targetType === 'FormField') {
            const params = state
              .getIn(['project', 'params'], List())
              .reduce((r, param) => r.push(param.get('id')), List())
              .toJS()
            let indexPath = String(
              _.get(node, 'data.loop[0].index.el', `loop:${node.idOrigin || node.id}:index`) ||
                `loop:${node.idOrigin || node.id}:index`
            ).split(':')
            let valuePath = String(
              _.get(node, 'data.loop[0].value.el', `loop:${node.idOrigin || node.id}:value`) ||
                `loop:${node.idOrigin || node.id}:value`
            ).split(':')

            //Index start from 1, it's clear for users
            if (indexPath) {
              if (params.includes(indexPath[0])) {
                state = state.setIn(['params', indexPath[0]], currentRule.index + 1)
              } else {
                state = state.setIn(['state', ...indexPath], currentRule.index + 1)
              }
            }
            if (valuePath) {
              if (targetType === 'FormField' && indexPath) {
                if (params.includes(valuePath[0])) {
                  state = state.setIn(['params', valuePath[0]], currentRule.index + 1)
                } else {
                  state = state.setIn(['state', ...valuePath], currentRule.index + 1)
                }
              } else {
                if (params.includes(valuePath[0])) {
                  state = state.setIn(['params', valuePath[0]], paramValues[currentRule.index] ?? '')
                } else {
                  state = state.setIn(['state', ...valuePath], paramValues[currentRule.index] ?? '')
                }
              }
            }
          }
          let nextNodes = _.uniqBy(getNextNode(list, node.id, node.actionType, null), 'id')
          _.each(nextNodes, (node) => {
            state = setupWaitNode(node, list, state, processUID)
            cacheList = cacheList.push(
              fromJS({
                type: 'FlowActionStep',
                payload: {
                  prevNodeID: currentNodeId,
                  list: list,
                  current: node.id,
                  index: currentRule.loopIndex,
                  currentRule,
                  currentSingleRule,
                  isOnlyEffects,
                  path: [],
                  processUID,
                  originIndex: currentRule.loopIndex,
                },
              })
            )
          })
          state = state.setIn(['__cache__'], cacheList)
          dispatch({
            type: actionTypes.SET_STORE,
            payload: {
              store: state,
            },
          })
        } else {
          currentRule.loopCaller = undefined
          originIndex = currentRule.originIndex = currentRule.loopOriginIndex
          index = currentRule.index = currentRule.loopOriginIndex
          if (window.__FT__APP__STATE?.__cache__?.[processUID]) {
            window.__FT__APP__STATE.__cache__[processUID] = undefined
          }
          let nextNodes = _.uniqBy(getNextNode(list, node.id, node.actionType, 'finish'), 'id')
          _.each(nextNodes, (node) => {
            state = setupWaitNode(node, list, state, processUID)
            cacheList = cacheList.push(
              fromJS({
                type: 'FlowActionStep',
                payload: {
                  prevNodeID: currentNodeId,
                  list: list,
                  current: node.id,
                  index: currentRule.loopOriginIndex,
                  currentRule,
                  currentSingleRule,
                  isOnlyEffects,
                  path,
                  processUID,
                  originIndex: currentRule.loopOriginIndex,
                },
              })
            )
          })
          state = state.setIn(['__cache__'], cacheList)
          dispatch({
            type: actionTypes.SET_STORE,
            payload: {
              store: state,
            },
          })
        }
        return null
      } else if (node.actionType === 'libFlow') {
        const id = _.get(node, 'data.libFlow.0.selected')
        state = getAppState()
        let cacheList = state.getIn(['__cache__'], List())
        if (id) {
          let actions = state.getIn(['project', 'Settings', 'library', id], Map())
          if (Map.isMap(actions) && actions.get('enabled') === false) {
            actions = Map()
          }
          const hash = actions.getIn(['actions', 'actionFlow', '0', 'list'], List()).reduce(
            (r, v) => ({
              ...r,
              [v.get('id')]: uuid(),
            }),
            {}
          )
          const nodesRaw = actions.getIn(['actions', 'actionFlow', '0', 'list'], List()).map((node) => {
            const nodeID = node.get('id')
            let out = node.set('id', hash[node.get('id')]).set('idOrigin', nodeID)
            if (out.get('source')) {
              out = out.set('source', hash[out.get('source')])
            }
            if (out.get('target')) {
              out = out.set('target', hash[out.get('target')])
            }
            return out
          })
          let nodes = nodesRaw
          let finishNodes = nodes.filter((node) => node.get('actionType') === 'flowFinish')
          let nextFinishNode = _.uniqBy([...getNextNode(list, node.id, node.actionType, 'finish')], 'id')
          if (finishNodes.size > 0 && nextFinishNode.length > 0) {
            finishNodes.map((node) => {
              _.map(nextFinishNode, (nextNode) => {
                nodes = nodes.push(
                  fromJS({
                    source: node.get('id'),
                    target: nextNode.id,
                    sourceHandle: 'finish',
                    type: 'default',
                    animated: false,
                    label: 'x',
                    labelStyle: { fontSize: 14 },
                    style: { strokeDasharray: 5 },
                    id: uuid(),
                  })
                )
              })
            })
          }
          let startNode = nodesRaw.find(
            (node) => node.get('type') === 'input' && node.getIn(['data', 'label']) === 'Start'
          )
          list = _.uniqBy([...list, ...nodes.toJS()], 'id')
          if (startNode) {
            cacheList = cacheList.push(
              fromJS({
                type: 'FlowActionStep',
                payload: {
                  prevNodeID: currentNodeId,
                  list: list,
                  current: startNode.get('id'),
                  index,
                  currentRule,
                  currentSingleRule,
                  isOnlyEffects,
                  path,
                  processUID,
                  originIndex: getOriginalIndex(),
                },
              })
            )
          }
        }
        let nextNode = _.uniqBy(
          [...getNextNode(list, node.id, node.actionType), ...getNextNode(list, node.id, node.actionType, null)],
          'id'
        )
        _.each(nextNode, (node) => {
          state = setupWaitNode(node, list, state, processUID)
          cacheList = cacheList.push(
            fromJS({
              type: 'FlowActionStep',
              payload: {
                prevNodeID: currentNodeId,
                list: list,
                current: node.id,
                index,
                currentRule,
                currentSingleRule,
                isOnlyEffects,
                path,
                processUID,
                originIndex: getOriginalIndex(),
              },
            })
          )
        })
        state = state.setIn(['__cache__'], cacheList)
        dispatch({
          type: actionTypes.SET_STORE,
          payload: {
            store: state,
          },
        })
      } else if (node.actionType === 'sfEvent') {
        let startProcessUID = processUID || node.processUID || uuid()
        let loadedNode = getNextNode(list, node.id, node.actionType, 'finish')
        let duringNode = getNextNode(list, node.id, node.actionType, null)

        let afterFinishActions = List()

        _.each(loadedNode, (node) => {
          state = setupWaitNode(node, list, state, processUID)

          afterFinishActions = afterFinishActions.push(
            fromJS({
              type: 'FlowActionStep',
              payload: {
                prevNodeID: currentNodeId,
                list: list,
                current: node.id,
                index,
                currentRule,
                currentSingleRule,
                isOnlyEffects,
                path,
                processUID: startProcessUID,
                originIndex: getOriginalIndex(),
              },
            })
          )
        })

        dispatch(processSfPlatformPush(_.get(node, ['data', 'sfEvent', '0']), afterFinishActions))

        _.each(duringNode, (node) => {
          state = setupWaitNode(node, list, state, processUID)
          state = state.setIn(
            ['__cache__'],
            state.getIn(['__cache__'], List()).push(
              fromJS({
                type: 'FlowActionStep',
                payload: {
                  prevNodeID: currentNodeId,
                  list: list,
                  current: node.id,
                  index,
                  currentRule,
                  currentSingleRule,
                  isOnlyEffects,
                  path,
                  oldState: oldStatePass,
                  oldParams: oldParamsPass,
                  processUID: startProcessUID,
                  originIndex: getOriginalIndex(),
                },
              })
            )
          )
        })

        dispatch({
          type: actionTypes.SET_STORE,
          payload: {
            store: state,
          },
        })
      } else if (node.actionType === 'WorkFlow') {
        _.set(node, ['data', 'WorkFlow', 0, 'flow'], {
          currentNode: node.id,
          list: list,
          index,
          isOnlyEffects,
        })
        let actions = fromJS(node.data)
        const oldState = oldStatePass || state.get('prevRulesState', Map()).toJS()
        const oldParams = oldParamsPass || state.get('prevRulesParams', Map()).toJS()
        let portalState = getState(state)
        let params = getParams(state)
        const processRuleTargets = getProcessTargetsFunction(
          state,
          fromJS(currentRule),
          isOnlyEffects,
          portalState,
          params,
          false,
          false,
          undefined,
          undefined,
          oldState,
          oldParams,
          path
        )
        const result = processRuleTargets(
          index,
          typeof index !== 'undefined' && index !== null,
          false,
          fromJS(currentSingleRule),
          actions
        )
        state = result.retState

        dispatch({
          type: actionTypes.SET_STORE,
          payload: {
            store: state,
          },
        })
      } else if (
        node.actionType === 'uac' &&
        ['otpLogin', 'otpVerify'].includes(_.get(node, ['data', 'uac', 0, 'action']))
      ) {
        let startProcessUID = processUID || node.processUID || uuid()
        let duringNode = getNextNode(list, node.id, node.actionType, null)
        let actions = {}
        if (_.get(node, ['data', 'uac', 0, 'action']) === 'otpLogin') {
          actions = {
            invalidInit: getNextNode(list, node.id, node.actionType, 'invalidInit'),
            validInit: getNextNode(list, node.id, node.actionType, 'validInit'),
            invalidLogin: getNextNode(list, node.id, node.actionType, 'invalidLogin'),
            validLogin: getNextNode(list, node.id, node.actionType, 'validLogin'),
            invalidVerify: getNextNode(list, node.id, node.actionType, 'invalidVerify'),
            validVerify: getNextNode(list, node.id, node.actionType, 'validVerify'),
          }
        } else if (_.get(node, ['data', 'uac', 0, 'action']) === 'otpVerify') {
          actions = {
            invalidVerify: getNextNode(list, node.id, node.actionType, 'invalidVerify'),
            validVerify: getNextNode(list, node.id, node.actionType, 'validVerify'),
          }
        }

        let hashJobs = {}
        _.each(actions, (loadedNode, key) => {
          let afterFinishActions = List()
          _.each(loadedNode, (node) => {
            state = setupWaitNode(node, list, state, processUID)

            afterFinishActions = afterFinishActions.push(
              fromJS({
                type: 'FlowActionStep',
                payload: {
                  prevNodeID: currentNodeId,
                  list: list,
                  current: node.id,
                  index,
                  currentRule,
                  currentSingleRule,
                  isOnlyEffects,
                  path,
                  processUID: startProcessUID,
                  originIndex: getOriginalIndex(),
                },
              })
            )
          })
          if (afterFinishActions.size) {
            hashJobs[key] = afterFinishActions
          }
        })
        if (_.get(node, ['data', 'uac', 0, 'action']) === 'otpLogin') {
          dispatch(processOtpLogin(hashJobs))
        } else if (_.get(node, ['data', 'uac', 0, 'action']) === 'otpVerify') {
          let pageUID = getPageUID(state.get('page'), state.getIn(['project', 'Pages'], List()))
          let elemUid
          let elements = state.getIn(['pages', state.get('page'), 'Props', 'elements'], Map()).toJS()
          let code
          _.each(elements, (elem, uid) => {
            if (elem && elem.type === 'SmartVOtpForm') {
              elemUid = uid
              code = state.getIn(['state', pageUID, uid])
            }
          })
          if (!elemUid) {
            elements = state.getIn(['project', 'Settings', 'elements'], Map()).toJS()
            _.each(elements, (elem, uid) => {
              if (elem && elem.type === 'SmartVOtpForm') {
                let parentElem = getParentIdInModal(state, elem.parent, true)
                code = state.getIn(['state', parentElem, uid])
              }
            })
          }
          if (!code) {
            if (actions.invalidVerify) {
              duringNode = duringNode.concat(actions.invalidVerify)
            }
          } else {
            dispatch(processOtpVerify(code, _.noop, hashJobs))
          }
        }

        _.each(duringNode, (node) => {
          state = setupWaitNode(node, list, state, processUID)
          state = state.setIn(
            ['__cache__'],
            state.getIn(['__cache__'], List()).push(
              fromJS({
                type: 'FlowActionStep',
                payload: {
                  prevNodeID: currentNodeId,
                  list: list,
                  current: node.id,
                  index,
                  currentRule,
                  currentSingleRule,
                  isOnlyEffects,
                  path,
                  oldState: oldStatePass,
                  oldParams: oldParamsPass,
                  processUID: startProcessUID,
                  originIndex: getOriginalIndex(),
                },
              })
            )
          )
        })

        dispatch({
          type: actionTypes.SET_STORE,
          payload: {
            store: state,
          },
        })
      } else if (node.actionType === 'Wait') {
        let originWaitList = getAppState().getIn(['__cond__cache__', 'wait', node.id], Map()).toJS()
        let waitList = getAppState().getIn(['__cond__cache__', 'wait', node.id], Map()).set(prevNodeID, true).toJS()
        let nodesList = _.keys(waitList)
        let nodesFinished = _.compact(_.values(waitList))
        let operationType = _.get(node, ['data', 'Wait', 0, 'operation'], 'all')
        if (operationType === 'any') {
          if (_.size(originWaitList)) {
            if (nodesList.length - 1 === nodesFinished.length && nodesList.includes('complete')) {
              dispatch({
                type: actionTypes.SET_STORE,
                payload: {
                  store: state.deleteIn(['__cond__cache__', 'wait', node.id]),
                },
              })
            } else {
              let nextNode = _.uniqBy(
                [...getNextNode(list, node.id, node.actionType), ...getNextNode(list, node.id, node.actionType, null)],
                'id'
              )
              let cacheList = state.getIn(['__cache__'], List())
              _.each(nextNode, (node) => {
                state = setupWaitNode(node, list, state, processUID)
                cacheList = cacheList.push(
                  fromJS({
                    type: 'FlowActionStep',
                    payload: {
                      prevNodeID: currentNodeId,
                      list: list,
                      current: node.id,
                      index,
                      currentRule,
                      currentSingleRule,
                      isOnlyEffects,
                      path,
                      processUID,
                      originIndex: getOriginalIndex(),
                    },
                  })
                )
              })
              dispatch({
                type: actionTypes.SET_STORE,
                payload: {
                  store: getAppState()
                    .setIn(['__cache__'], cacheList)
                    .setIn(['__cond__cache__', 'wait', node.id, prevNodeID], true)
                    .setIn(['__cond__cache__', 'wait', node.id, 'complete'], true),
                },
              })
            }
          }
        } else if (nodesList.length === nodesFinished.length && operationType === 'all') {
          let nextNode = _.uniqBy(
            [...getNextNode(list, node.id, node.actionType), ...getNextNode(list, node.id, node.actionType, null)],
            'id'
          )
          let cacheList = state.getIn(['__cache__'], List())
          _.each(nextNode, (node) => {
            state = setupWaitNode(node, list, state, processUID)
            cacheList = cacheList.push(
              fromJS({
                type: 'FlowActionStep',
                payload: {
                  prevNodeID: currentNodeId,
                  list: list,
                  current: node.id,
                  index,
                  currentRule,
                  currentSingleRule,
                  isOnlyEffects,
                  path,
                  processUID,
                  originIndex: getOriginalIndex(),
                },
              })
            )
          })
          state = state.setIn(['__cache__'], cacheList).deleteIn(['__cond__cache__', 'wait', node.id])
          dispatch({
            type: actionTypes.SET_STORE,
            payload: {
              store: state,
            },
          })
        } else {
          dispatch({
            type: actionTypes.SET_STORE,
            payload: {
              store: getAppState().setIn(['__cond__cache__', 'wait', node.id, prevNodeID], true),
            },
          })
        }
        // state = setupWaitNode(node, list, state, processUID)
      } else {
        if (node.type === 'affectElements' && (index === null || index === undefined)) {
          let repeatRoot = state.getIn(['project', 'Settings', 'RepeatedStripsID'], List()).toJS()
          let listIDs = state
            .getIn(['project', 'Settings', 'elements'], Map())
            .reduce(
              (r, v, id) => {
                if (v.get('type') === 'PowerTable' || v.get('type') === 'InteractiveImage') {
                  return r.push(id)
                } else if (v.get('type') === 'Widget' && v.getIn(['props', 'data', 'Type']) === 'list') {
                  return r.push(id)
                } else {
                  return r
                }
              },
              state.getIn(['pages', state.get('page'), 'Props', 'elements'], Map()).reduce((r, v, id) => {
                if (v.get('type') === 'PowerTable' || v.get('type') === 'InteractiveImage') {
                  return r.push(id)
                } else if (v.get('type') === 'Widget' && v.getIn(['props', 'data', 'Type']) === 'list') {
                  return r.push(id)
                } else {
                  return r
                }
              }, List())
            )
            .toJS()
          let tableIDs = state
            .getIn(['project', 'Settings', 'elements'], Map())
            .reduce(
              (r, v, id) => {
                if (v.get('type') === 'Widget' && v.getIn(['props', 'data', 'Type']) === 'table') {
                  return r.push(id)
                } else {
                  return r
                }
              },
              state.getIn(['pages', state.get('page'), 'Props', 'elements'], Map()).reduce((r, v, id) => {
                if (v.get('type') === 'Widget' && v.getIn(['props', 'data', 'Type']) === 'table') {
                  return r.push(id)
                } else {
                  return r
                }
              }, List())
            )
            .toJS()
          let cacheList = List()
          const pageParams = state
            .getIn(['pages', state.get('page'), 'Props', 'params'], List())
            .map((param) => param.get('id'))
            .toJS()
          const projectParams = state
            .getIn(['project', 'params'], List())
            .map((param) => param.get('id'))
            .toJS()

          const allParamsID = [...pageParams, ...projectParams]
          let portalState = getState(state)
          let originState = state.get('state', Map()).toJS()
          let params = getParams(state)

          let isRepeat = false
          let isList = false /// list + powertable
          let isTable = false //widget
          let stateKey = null
          _.each(_.get(node, ['data'], {}), (action, actionKey) => {
            let key = String(actionKey).split(':')
            if (repeatRoot.includes(key[1])) {
              isRepeat = true
              stateKey = actionKey
            } else if (listIDs.includes(key[1]) && key.length > 2) {
              isList = true
              stateKey = actionKey
            } else if (tableIDs.includes(key[1]) && key.length > 2) {
              isTable = true
              stateKey = actionKey
            }
          })

          if (isList || isTable || isRepeat) {
            let key = String(stateKey).split(':')
            let rows = []
            if (isList) {
              rows = _.get(originState, [key[0], key[1]], [])
            } else if (isTable) {
              rows = _.get(originState, key.length > 2 ? _.initial(key) : key, [])
            } else if (isRepeat) {
              rows = _.get(originState, _.slice(key, 0, 2), [])
            }
            if (isRepeat) {
              let num = getMappingCount(rows)
              if (!num) {
                num = 1
              }
              rows = _.times(num, (n) => n)
            } else {
              if (isList) {
                let keys = _.keys(rows)
                if (_.isArray(rows[keys[0]])) {
                  rows = _.times(_.size(rows[keys[0]]), (n) => {
                    return _.reduce(
                      keys,
                      (r, v) => ({
                        ...r,
                        [v]: _.get(rows, [v, n], ''),
                      }),
                      {}
                    )
                  })
                } else {
                  rows = [rows]
                }
              } else if (!_.isArray(rows)) {
                rows = [rows]
              }
            }
            if (isTable && String(stateKey).split(':').length === 2) {
              state = doProcessRules(state, false, [], [], [String(stateKey).split(':')], false, false, null)
            }
            _.each(rows, (row, index) => {
              let actions = fromJS(node.data)
              const oldState = oldStatePass || state.get('prevRulesState', Map()).toJS()
              const oldParams = oldParamsPass || state.get('prevRulesParams', Map()).toJS()
              const processRuleTargets = getProcessTargetsFunction(
                state,
                fromJS(currentRule),
                isOnlyEffects,
                portalState,
                params,
                false,
                false,
                undefined,
                undefined,
                oldState,
                oldParams,
                path
              )
              const result = processRuleTargets(
                index,
                typeof index !== 'undefined' && index !== null,
                false,
                fromJS(currentSingleRule),
                actions
              )
              state = result.retState
              let changes = _.uniqBy(state.getIn(['ft', 'changes'], List()).toJS(), (v) => v.join(':'))
              state = state.setIn(['ft', 'changes'], List())
              if (typeof index !== 'undefined' && index !== null) {
                changes = _.map(changes, (path) => {
                  if (allParamsID.includes(path[1]) && !currentRule.isTableRule) {
                    return _.initial(path)
                  } else {
                    return path
                  }
                })
              }
              const [st, newChanges] = recalculatePowerFields(state, changes)
              changes.push(...newChanges)
              state = st
              const appState = _.assign({}, portalState, params)
              const freshState = _.assign({}, getState(state, true), getParams(state))
              let isRunRules = false
              _.each(changes, (path) => {
                let oldValue = _.get(appState, path)
                let newValue = _.get(freshState, path)
                if (!shallowEqual(oldValue, newValue)) {
                  isRunRules = true
                  return false
                }
              })
              if (isRunRules && changes.length > 0) {
                if (index === currentRule.index) {
                  state = doProcessRules(state, false, [], [], changes, false, false, index)
                } else {
                  try {
                    let localChanges = [],
                      globalChanges = []
                    let hashParams = getHashActiveParams(state)
                    _.each(changes, (paramPath) => {
                      if (paramPath.length == 1) {
                        globalChanges.push(paramPath)
                      } else {
                        if (hashParams[paramPath[0]] || hashParams[paramPath[1]]) {
                          globalChanges.push(paramPath)
                        } else {
                          localChanges.push(paramPath)
                        }
                      }
                    })
                    if (globalChanges.length) {
                      state = doProcessRules(state, false, [], [], changes, false, false, currentRule.index)
                    }
                    if (localChanges.length) {
                      state = doProcessRules(state, false, [], [], changes, false, false, index)
                    }
                  } catch (err) {}
                }
              }
              let nextNode = _.uniqBy(
                [...getNextNode(list, node.id, node.actionType), ...getNextNode(list, node.id, node.actionType, null)],
                'id'
              )
              // cacheList = List()
              _.each(nextNode, (node) => {
                state = setupWaitNode(node, list, state, processUID)
                cacheList = cacheList.push(
                  fromJS({
                    prevNodeID: currentNodeId,
                    list: list,
                    current: node.id,
                    index,
                    currentRule,
                    currentSingleRule,
                    isOnlyEffects,
                    path,
                    processUID,
                    originIndex: getOriginalIndex(),
                  })
                )
              })
            })
            dispatch({
              type: actionTypes.SET_STORE,
              payload: {
                store: state,
              },
            })
            cacheList.map((entry) => {
              processRunActionFlowNodeEntry(entry, dispatch)
            })
            return false
          }
        }

        const pageParams = state
          .getIn(['pages', state.get('page'), 'Props', 'params'], List())
          .map((param) => param.get('id'))
          .toJS()
        const projectParams = state
          .getIn(['project', 'params'], List())
          .map((param) => param.get('id'))
          .toJS()
        const allParamsID = [...pageParams, ...projectParams]
        let portalState = getState(state)
        let params = getParams(state)
        const updateNodeData = (key, prop = 'msgDlg', keyPath = []) => {
          let startNode = _.find(list, (node) => node.type === 'input')
          let actionList = _.filter(list, (node) => node.source !== startNode.id)
          startNode.newId = currentNodeId
          startNode.processUID = processUID
          let confAction = getNextNode(list, node.id, node.actionType, key)
          _.each(confAction, (node) => {
            state = setupWaitNode(node, list, state, processUID)
            actionList.push({
              type: 'default',
              targetHandle: 'if',
              target: node.id,
              source: startNode.id,
              id: uuid(),
            })
          })
          let flowAction = {
            uid: uuid(),
            conditions: [],
            isAction: false,
            index: index,
            actions: {
              actionFlow: {
                0: {
                  enabled: true,
                  list: actionList,
                },
              },
            },
            modeType: 'flow',
            options: currentRule.options || currentSingleRule.options || {},
          }
          _.set(node, ['data', prop, 0, ...keyPath, key], _.get(node, ['data', prop, 0, ...keyPath, key], []))
          let targetActionList = _.get(node, ['data', prop, 0, ...keyPath, key], [])
          if (!_.isArray(targetActionList)) {
            _.set(node, ['data', prop, 0, ...keyPath, key], [])
            targetActionList = _.get(node, ['data', prop, 0, ...keyPath, key], [])
          }
          targetActionList.unshift(flowAction)
          _.set(node, ['data', prop, 0, ...keyPath, key], targetActionList)
        }
        if (
          ['msgDlgtoast', 'msgDlgconfirm', 'msgDlgslack', 'msgDlg'].includes(node.actionType) &&
          node?.data?.msgDlg?.[0]
        ) {
          if (
            _.get(node, 'data.msgDlg.0.variant', 'message') === 'message' &&
            ['successMsg', 'errorMsg'].includes(_.get(node, 'data.msgDlg.0.typeOfModal')) &&
            _.get(node, 'data.msgDlg.0.allowBtnClose', true)
          ) {
            updateNodeData('btnCloseAction')
          } else if (_.get(node, 'data.msgDlg.0.variant', 'message') === 'confirm') {
            updateNodeData('confAction')
            updateNodeData('cancelAction')
          } else if (_.get(node, 'data.msgDlg.0.variant', 'message') === 'toast') {
            if (_.get(node, 'data.msgDlg.0.allowBtn1')) {
              updateNodeData('btn1Action')
            }
            if (_.get(node, 'data.msgDlg.0.allowBtn2')) {
              updateNodeData('btn2Action')
            }
          }
        } else if (node.actionType === 'docGen' && node?.data?.docGen?.[0]) {
          if (node?.data?.docGen?.[0]?.action === 'preview') {
            let actions = state.getIn(
              ['project', 'Settings', 'store', 'public', node?.data?.docGen?.[0]?.selected, 'previewActions'],
              List()
            )
            actions.map((action) => {
              updateNodeData(action.get('uid'), 'docGen', ['previewActionsList'])
            })
          } else if (node?.data?.docGen?.[0]?.action === 'interactive') {
            updateNodeData('onSubmit', 'docGen')
            updateNodeData('onClose', 'docGen')
          } else if (
            _.get(node, ['data', 'docGen', 0, 'method'], 'titanSign') === 'titanSign' &&
            _.get(node, ['data', 'docGen', 0, 'titanSignAction'], 'sign') === 'sign'
          ) {
            updateNodeData('sign', 'docGen')
            updateNodeData('signCancel', 'docGen')
          }
        } else if (node.actionType === 'timerInteractivity' && node?.data?.timerInteractivity?.[0]) {
          updateNodeData('onTimeout', 'timerInteractivity')
          updateNodeData('onFinish', 'timerInteractivity')
        } else if (node.actionType === 'validate' && node?.data?.validate?.[0]) {
          updateNodeData('onValid', 'validate')
          updateNodeData('onInValid', 'validate')
        } else if (
          node.actionType === 'formInteractivity' &&
          node?.data?.formInteractivity?.[0]?.action === 'Is Changed'
        ) {
          updateNodeData('onChanged', 'formInteractivity')
          updateNodeData('onNotChanged', 'formInteractivity')
        } else if (
          node.actionType === 'formInteractivity' &&
          node?.data?.formInteractivity?.[0]?.action === 'Validate fields'
        ) {
          updateNodeData('onValid', 'formInteractivity')
          updateNodeData('onInValid', 'formInteractivity')
        } else if (node.actionType === 'sendEmail') {
          updateNodeData('fail', 'sendEmail')
          updateNodeData('success', 'sendEmail')
        }
        let actions = fromJS(node.data)
        const oldState = oldStatePass || state.get('prevRulesState', Map()).toJS()
        const oldParams = oldParamsPass || state.get('prevRulesParams', Map()).toJS()
        currentRule.nodeId = node.id
        currentRule.currentSingleRule = currentSingleRule
        currentRule.path = path
        currentRule.processUID = processUID
        currentSingleRule.options = currentSingleRule.options || currentRule.options || {}
        const processRuleTargets = getProcessTargetsFunction(
          state,
          fromJS(currentRule),
          isOnlyEffects,
          portalState,
          params,
          false,
          false,
          undefined,
          undefined,
          oldState,
          oldParams,
          path,
          getOriginalIndex()
        )
        let localActionIndex = index
        if (currentRule && (currentRule.fieldType == 'PowerTable' || currentRule.fieldType == 'InteractiveImage')) {
          try {
            if (typeof localActionIndex !== 'undefined' && localActionIndex !== null) {
              if (node.type === 'affectElements') {
                let isAffectPowerTable = false
                actions.map((row) => {
                  if (row && List.isList(row)) {
                    row.map((subRow) => {
                      if (
                        !row.getIn([0, 'elemType']) ||
                        row.getIn([0, 'elemType']) == 'rowStyle' ||
                        row.getIn([0, 'elemType']) == 'listRowStyle' ||
                        row.getIn([0, 'elemType']) == 'param' ||
                        row.getIn([0, 'elemType']) == 'pageParam' ||
                        row.getIn([0, 'elemType']) == 'PowerTable' ||
                        row.getIn([0, 'type']) == 'set_param_from' ||
                        row.getIn([0, 'type']) == 'set_value_from' ||
                        row.getIn([0, 'from', 'type']) == 'InteractiveImage' ||
                        row.getIn([0, 'from', 'type']) == 'PowerTable'
                      ) {
                        isAffectPowerTable = true
                      }
                    })
                  }
                })
                if (!isAffectPowerTable) {
                  localActionIndex = null
                }
              }
            }
          } catch (err) {}
        }
        const result = processRuleTargets(
          localActionIndex,
          typeof localActionIndex !== 'undefined' && localActionIndex !== null,
          false,
          fromJS(currentSingleRule),
          actions
        )
        state = result.retState
        let localAction
        if (isImmutable(actions)) {
          try {
            localAction = actions.getIn(['TabInter', '0', 'action'])
          } catch (err) {
            console.error(err)
          }
        }
        if (!shouldDisableRecalculation(localAction)) {
          let changes = _.uniqBy(state.getIn(['ft', 'changes'], List()).toJS(), (v) => v.join(':'))
          state = state.setIn(['ft', 'changes'], List())
          if (typeof index !== 'undefined' && index !== null) {
            changes = _.map(changes, (path) => {
              if (allParamsID.includes(path[1]) && !currentRule.isTableRule) {
                return _.initial(path)
              } else {
                return path
              }
            })
          }
          if (changes.length > 0) {
            const [st, newChanges] = recalculatePowerFields(state, changes)
            changes.push(...newChanges)
            state = st
          }
          const appState = _.assign({}, portalState, params)
          const freshState = _.assign({}, getState(state, true), getParams(state))
          let isRunRules = false
          _.each(changes, (path) => {
            let oldValue = _.get(appState, path)
            let newValue = _.get(freshState, path)
            if (!shallowEqual(oldValue, newValue)) {
              isRunRules = true
              return false
            }
          })
          if (isRunRules && changes.length > 0) {
            let localIndex = index
            if (currentRule && (currentRule.fieldType == 'PowerTable' || currentRule.fieldType == 'InteractiveImage')) {
              localIndex = null
            }
            state = doProcessRules(state, false, [], [], changes, false, false, localIndex, null, {
              processUID,
              runID: currentRule?.options?.runID || currentRule.uid,
              clientY: currentRule?.options?.clientY,
            })
          }
        }
        let nextNode = _.uniqBy(
          [...getNextNode(list, node.id, node.actionType), ...getNextNode(list, node.id, node.actionType, null)],
          'id'
        )
        let cacheList = state.getIn(['__cache__'], List())
        _.each(nextNode, (node) => {
          state = setupWaitNode(node, list, state, processUID)
          cacheList = cacheList.push(
            fromJS({
              prevNodeID: currentNodeId,
              list: list,
              current: node.id,
              index,
              currentRule,
              currentSingleRule,
              isOnlyEffects,
              path,
              processUID,
              originIndex: getOriginalIndex(),
            })
          )
        })
        dispatch({
          type: actionTypes.SET_STORE,
          payload: {
            store: state,
          },
        })
        cacheList.map((entry) => {
          processRunActionFlowNodeEntry(entry, dispatch)
        })
      }
    }
    const end = performance.now()

    window.totalTime = window.totalTime || 0;
    window.executionStats = window.executionStats || {};

    const recordExecutionTime = (node, start, end, currentRule) => {
      const uid = `${node?.tag ?? node?.actionType ?? 'unknown'}_${node?.id}`;
      const executionTime = (end - start) / 1000;
      window.totalTime += executionTime;

      if (!window.executionStats[uid]) {
        window.executionStats[uid] = {total: 0, longest: 0, currentRule};
      }

      window.executionStats[uid].total += executionTime;
      window.executionStats[uid].longest = Math.max(window.executionStats[uid].longest, executionTime);
    }

    recordExecutionTime(node, start, end, currentRule);
  }

  if(!IS_SERVER) {
    window.ost = outputActionFlowSummary
  }

const clearBtnState = () => (dispatch, getState) => {
  let state = getState()
  let appState = state.get('state', Map())

  const clearBtnStatus = (val, key) => {
    if (key === 'BTN_STATE') {
      return 'idle'
    } else if (isImmutable(val)) {
      return val.map(clearBtnStatus)
    } else {
      return val
    }
  }

  appState = appState.map(clearBtnStatus)
  state = state.set('state', appState)
  dispatch({
    type: actionTypes.SET_STORE,
    payload: { store: state },
  })
}

const clearState = () => (dispatch, getState) => {
  let state = getState()
  let pageID = state.get('page')
  let oldPage = state.getIn(['pages', pageID], Map())
  let oldPageUID
  state.getIn(['project', 'Pages'], List()).map((page) => {
    if (page.get('_id') == pageID) {
      oldPageUID = page.get('uid')
    }
  })
  if (oldPageUID && oldPage) {
    oldPage.getIn(['Props', 'params'], List()).map((param) => {
      state = state.setIn(['state', oldPageUID, param.get('id')], '')
    })

    oldPage.getIn(['Props', 'elements'], List()).map((elem, uid) => {
      if (elem.get('type') == 'InteractiveDocument') {
        if (elem.getIn(['props', 'genSource']) != 'titan') {
          state = state.setIn(['state', oldPageUID], state.getIn(['state', oldPageUID], Map()).delete(uid))
        }
      }
      if (elem.get('type') == 'FormField') {
        state = state.setIn(['state', oldPageUID], state.getIn(['state', oldPageUID], Map()).delete(uid))
      }
    })
  }
  dispatch({
    type: actionTypes.SET_STORE,
    payload: {
      store: state.setIn(['isLogoutProcess'], true),
    },
  })
}

export const updateBarcodeState = (entry, value) => (dispatch, getState) => {
  let state = getState()

  const prePath = entry.isRepeat ? [entry.PageUID, entry.repeatUID, entry.uid] : [entry.PageUID, entry.uid]
  const postPath = entry.isRepeat ? [entry.index] : []

  const changes = []

  _.each(value, (val, key) => {
    state = state.setIn(['state', ...prePath, key, ...postPath], val)
    changes.push([...prePath, key, ...postPath])
  })

  state = doProcessRules(state, true, [], [], changes, false, false)

  dispatch({
    type: actionTypes.SET_STORE,
    payload: {
      store: state,
    },
  })
  dispatch(processButtonAction(fromJS(entry.action), entry.uid, entry.isRepeat, entry.index))
}

const readBarcodeAPI = (entry, dispatch) => {
  let control = document.createElement('input')
  control.setAttribute('type', 'file')
  control.setAttribute('capture', `user`)
  control.setAttribute('accept', 'image/*')

  const onChange = async (event) => {
    let file = control.files[0]
    let uidPath = [entry.uid]
    if (entry.isRepeat) {
      uidPath = [entry.repeatUID, entry.uid, String(entry.index)]
    }
    if (!file) {
      dispatch(updateBarcodeState(entry, { status: 'error', value: 'No File', valueType: '' }))
      return null
    }
    let mimeType = file.type
    if (!['image/jpeg', 'image/png', 'image/gif'].includes(mimeType)) {
      dispatch(updateBarcodeState(entry, { status: 'error', value: 'Not A Barcode', valueType: '' }))
      return null
    }
    if (file.size > 2 * 1024 * 1024) {
      file = await resizeBase64Img(file, { width: 1200, height: 1200, quality: 100 })
    }

    try {
      const axios = getAxios()
      let formData = new FormData()
      formData.append('file', file)
      let { status, data } = await axios.post('/webprojects/readBarcode', formData, {
        'Content-Type': 'multipart/form-data',
      })
      if (status === 200 && data.status && _.get(data, 'value.Successful')) {
        dispatch(
          updateBarcodeState(entry, {
            status: 'ok',
            value: _.get(data, 'value.RawText', ''),
            valueType: _.get(data, 'value.BarcodeType', ''),
          })
        )
      } else {
        dispatch(updateBarcodeState(entry, { status: 'error', value: 'Not A Barcode', valueType: '' }))
      }
    } catch (error) {
      dispatch(updateBarcodeState(entry, { status: 'error', value: 'Server Error', valueType: '' }))
    }
  }

  control.addEventListener('change', onChange)
  control.click()
}

const processRunActionFlowNodeEntry = (entry, dispatch) => {
  const payload = entry.toJS()
  const list = payload.list || []
  const current = payload.current
  const index = payload.index
  const currentRule = payload.currentRule
  const currentSingleRule = payload.currentSingleRule
  const isOnlyEffects = payload.isOnlyEffects
  const path = payload.path
  const oldState = payload.oldState
  const oldParams = payload.oldParams
  const prevNodeID = payload.prevNodeID
  const processUID = payload.processUID || uuid()
  let originIndex = payload.originIndex
  if (originIndex === undefined) {
    originIndex = index
  }

  dispatch(
    processActionFlowNode(
      current,
      list,
      index,
      currentRule,
      currentSingleRule,
      isOnlyEffects,
      path,
      oldState,
      oldParams,
      prevNodeID,
      processUID,
      originIndex
    )
  )
}

export const processCondCache = () => async (dispatch, getState) => {
  let inCache = getState().getIn(['__cache__'], List())
  let isChatAI = false,
    isActionProcess = false
  const cache = inCache.filter((v) => {
    if (v.get('type') == 'ChatBotAIAction') {
      isChatAI = true
    }
    return CUSTOM_ACTION_TYPES.indexOf(v.get('type')) === -1
  })
  const emailCache = inCache.filter((v) => CUSTOM_ACTION_TYPES.indexOf(v.get('type')) !== -1)

  if (cache.filter((v) => STATIC_COND_CACHE.includes(v.get('type'))).size > 0) {
    return dispatch({
      type: actionTypes.PROJECT_PROCESS_COND_CACHE,
    })
  }

  dispatch({
    type: actionTypes.SET_STORE,
    payload: {
      store: getState().set('__cache__', List()),
    },
  })

  processSendCustomAction(emailCache, getState(), dispatch)

  const processedTabOnClick = []
  let isRunAutoTrigger = false
  cache.map((entry) => {
    try {
      switch (entry.get('type')) {
        case 'redirectToEnvParams': {
          if (entry.getIn(['payload', 'uid'])) {
            fetchEnvParameter(entry.getIn(['payload', 'uid'])).then((result) => {
              if (result.status) {
                let newEntry = entry.set('type', 'redirect')
                let paramSep = result.url.indexOf('?') !== -1 ? '&' : '?'
                let params = newEntry.getIn(['payload', 'params'])
                let addParams = params === '' || typeof params === 'undefined' ? '' : paramSep + params
                newEntry = newEntry.setIn(['payload', 'uid'], '')
                newEntry = newEntry.setIn(['payload', 'uri'], result.url + addParams)
                let state = getState()

                let cacheList = state.getIn(['__cache__'], List())
                cacheList = cacheList.push(newEntry)

                dispatch({
                  type: actionTypes.SET_STORE,
                  payload: {
                    store: state.set('__cache__', cacheList),
                  },
                })
              }
            })
          }
          break
        }
        case 'runAsyncAction': {
          let index = entry.getIn(['payload', 'index'])
          dispatch(
            processButtonAction(
              fromJS(entry.getIn(['payload', 'action'], List())),
              uuid(),
              typeof index === 'number',
              index,
              toJS(entry.getIn(['payload', 'options'], Map()))
            )
          )
          break
        }
        case 'processSFAutoTrigger': {
          if (!isRunAutoTrigger) {
            isRunAutoTrigger = true
            dispatch(processSFAutoTrigger(false, 0, false, entry.getIn(['payload', 'index'])))
          }
          break
        }
        case 'paymentIntegration': {
          dispatch(
            processPaymentIntegration({
              actionID: entry.getIn(['payload', 'uid']),
              currentRule: entry.getIn(['payload', 'currentRule'], Map()).toJS(),
            })
          )
          break
        }
        case 'revealInTabsContainer': {
          try {
            dispatch(revealInTabsContainer(entry.getIn(['payload', 'elemUID']), entry.getIn(['payload', 'index'])))
          } catch (error) {
            console.error(error)
          }
          break
        }
        case 'OpenPrivateModal': {
          dispatch(openPrivateModal(entry.getIn(['payload'])))
          break
        }
        case 'surveySubmit': {
          const flowSlides = entry.getIn(['payload', 'options', 'flowSlides'], List()).toJS()
          const isPartial = entry.getIn(['payload', 'options', 'isPartial'])
          const slide = entry.getIn(['payload', 'options', 'slide'], Map()).toJS()
          const isLast = entry.getIn(['payload', 'options', 'isLast'])
          const runInBackground = entry.getIn(['payload', 'options', 'runInBackground'])
          const action = entry.getIn(['payload', 'options', 'action'], Map())
          const uid = entry.getIn(['payload', 'options', 'uid'])
          const isRepeat = entry.getIn(['payload', 'options', 'isRepeat'])
          const index = entry.getIn(['payload', 'options', 'index'])
          dispatch(processSurveyFinish(flowSlides, isPartial, slide, isLast, runInBackground)).then(() => {
            dispatch(processButtonAction(List.of(action), uid, isRepeat, index))
          })
          break
        }
        case 'FlowActionStep': {
          const payload = entry.getIn(['payload'], Map())
          processRunActionFlowNodeEntry(payload, dispatch)
          break
        }
        case 'pwaClearSfCache': {
          sfClearCacheData()
          break
        }
        case 'addCartItem': {
          const onUpdate = entry.getIn(['payload', 'onUpdate'], List())
          const onAdd = entry.getIn(['payload', 'onAdd'], List())
          dispatch(addToCart(entry.getIn(['payload', 'item'], Map()).toJS(), 1, onAdd, onUpdate))
          break
        }
        case 'pwaUpdateSfCache': {
          dispatch(sfFetchCacheData(true))
          break
        }
        case 'ReadBarcode': {
          readBarcodeAPI(entry.getIn(['payload'], Map()).toJS(), dispatch)
          break
        }
        case 'RefreshGeoLoc': {
          dispatch(projectPreviewDetectLocationAPI())
          break
        }
        case 'ValidateAction': {
          dispatch(processValidateAction(entry.getIn(['payload'], Map()).toJS()))
          break
        }
        case 'LoginActionRedirect': {
          requestAnimationFrame(() => {
            const state = getState()
            let currentPage = state
              .getIn(['project', 'Pages'], List())
              .find((page) => page.get('_id') === state.get('page'))
            const homePage = state.getIn(['project', 'Pages'], List()).find((page) => page.get('isHome'))
            if (currentPage) {
              window.__FT_PROJECT__API__.goToPage(currentPage.get('_id'), '', true)
            } else if (homePage && window.__FT_PROJECT__API__) {
              window.__FT_PROJECT__API__.goToPage(homePage.get('_id'), '', true)
            } else if (window.__FT_PROJECT__API__ && _.endsWith(window.location.href, '/signin')) {
              window.__FT_PROJECT__API__.FTRedirectToProjectURL('/')
            } else if (
              window.__FT_PROJECT__API__ &&
              state.getIn(['project', 'Settings', 'isCustomDomainOpened'], false) &&
              _.endsWith(window.location.href, '/signin')
            ) {
              window.__FT_PROJECT__API__.FTRedirectToProjectURL('/')
            } else if (window.__FT_PROJECT__API__ && _.endsWith(window.location.href, '/signin')) {
              window.__FT_PROJECT__API__.FTRedirectToProjectURL('/')
            } else if (window.__FT_PROJECT__API__) {
              window.__FT_PROJECT__API__.FTRedirectToProjectURL('/')
            } else {
              window.location.reload()
            }
          })
          break
        }
        case 'FTReloadPage': {
          let state = getState()
          if (typeof window.__FT_PROJECT__API__?.goToPage === 'function') {
            let pageUID = getPageUID(state.get('page'), state.getIn(['project', 'Pages'], List()))
            window.__FT_PROJECT__API__.goToPage(pageUID, '', true)
          }
          break
        }
        case 'SmartVProcessActualLogout': {
          let state = getState()
          ///for case if need additional check before process logout
          // const isSSOPrivate = state.getIn(['project', 'Settings', 'isSSOPrivate'], false)
          // let haveSession = Boolean(getPreviewDynamicData()?.user)
          // if (!haveSession) {
          //     if (isSSOPrivate) {
          //         haveSession = Boolean(_.get(getPreviewDynamicData(), ['ssoParams', 'data']))
          //     }
          // }
          // if (!haveSession) {
          //     dispatch(clearState())
          //     state = getState()
          //     state = state
          //         .setIn(['isLocalLogout'], true)
          //         .setIn(['isLogoutProcess'], false);

          //     ftRemoveLocalStorage(SESSION_TOKEN_NAME)
          //     ftRemoveLocalStorage(SESSION_SALT_NAME)

          //     return dispatch({
          //         type: actionTypes.SET_STORE,
          //         payload: {
          //             store: state
          //         }
          //     })
          // }
          if (
            state.getIn(['project', 'Settings', 'ui', 'events', 'isUseBeforeLogoutEvent'], false) &&
            state.getIn(['project', 'Settings', 'ui', 'events', 'onBeforeLogOut'], List()).size > 0
          ) {
            dispatch(clearState())
            state = getState()
          }
          requestAnimationFrame(() => {
            let logoutPromise = Promise.resolve(true)

            Promise.all([logoutPromise, webProjectSmartvLogout(state.getIn(['project', 'URL'], ''))]).then(() => {
              const modals = state.getIn(['project', 'Settings', 'ui', 'Modal'], List())
              modals.map((uid) => {
                state = state.setIn(['state', 'project', uid], true)
              })
              dispatch({
                type: actionTypes.SET_STORE,
                payload: {
                  store: state,
                },
              })
              ftRemoveLocalStorage(SESSION_TOKEN_NAME)
              ftRemoveLocalStorage(SESSION_SALT_NAME)
              state = getState()
              try {
                __FT_PROJECT__API__.FTRefreshPWAPages()
              } catch (error) {
                console.error(error)
              }
              let onLogOut = List()
              if (state.getIn(['project', 'Settings', 'ui', 'events', 'isUseOnLogoutEvent'], false)) {
                onLogOut = state.getIn(['project', 'Settings', 'ui', 'events', 'onLogOut'], List())
              }
              const isHaveRedirect = onLogOut.find((action) => {
                let act = action.toJS()
                if (act.modeType === 'flow') {
                  let list = _.get(act, ['actions', 'actionFlow', 0, 'list'], [])
                  let redirectNodes = _.filter(
                    list,
                    (node) =>
                      (_.get(node, ['data', 'redirect', 0, 'enabled']) &&
                        _.get(node, ['data', 'redirect', 0, 'customURL'])) ||
                      _.get(node, ['data', 'redirect', 0, 'redirect', 'value']) ||
                      _.get(node, ['data', 'reloadPage', 0, 'enabled'])
                  )
                  return redirectNodes.length > 0
                } else {
                  return (
                    (_.get(act, ['actions', 'redirect', 0, 'enabled']) &&
                      _.get(act, ['actions', 'redirect', 0, 'customURL'])) ||
                    _.get(act, ['actions', 'redirect', 0, 'redirect', 'value']) ||
                    _.get(act, ['actions', 'reloadPage', 0, 'enabled'])
                  )
                }
              })

              clearPreviewUserData()
              state = state.setIn(['params', 'isLogged'], false)
              if (state.getIn(['project', 'Settings', 'otpSettings', 'enabled'], false)) {
                state = state
                  .deleteIn(['params', 'ftOtpSessionRemainingTimeSeconds'])
                  .deleteIn(['params', 'ftOtpSessionStartTimestamp'])
                  .deleteIn(['params', 'ftOtpSessionDurationMinutes'])
              }
              if (state.getIn(['project', 'Settings', 'ac', 'enabled'])) {
                const paramID =
                  state
                    .getIn(['project', 'params'], List())
                    .find((param) => param.get('isACParam') === true)
                    ?.get?.('id') || '_'
                state = state.setIn(['params', paramID], '')
                state = processApplyOnloadACRules(state, true)
              }
              let changes = [['isLogged']]
              state.getIn(['project', 'Settings', 'SmartV', 'params'], Map()).map((v, k) => {
                state = state.deleteIn(['params', k])
                changes.push([k])
              })
              if (changes.length > 0) {
                state = doProcessRules(
                  state,
                  Boolean(_.get(window.__FT_APP_STATE__, 'portal')),
                  [],
                  [],
                  changes,
                  false,
                  false
                )
                dispatch({
                  type: actionTypes.SET_STORE,
                  payload: {
                    store: state,
                  },
                })
              } else {
                dispatch({
                  type: actionTypes.SET_STORE,
                  payload: {
                    store: state,
                  },
                })
              }
              ftRemoveLocalStorage(getSessionProjectLocaKey(state.getIn(['project', 'URL'], '')))
              if (!isHaveRedirect) {
                onLogOut = onLogOut.unshift(
                  fromJS({
                    uid: 'login-post-event',
                    conditions: [],
                    isAction: false,
                    actions: {
                      LoginAction: {
                        0: {
                          enabled: true,
                        },
                      },
                    },
                  })
                )
              }
              dispatch(processButtonAction(onLogOut, 'SmartVEventComplete', false, null))
              state = getState()
              state = state.setIn(['isLocalLogout'], true).setIn(['isLogoutProcess'], false)
              dispatch({
                type: actionTypes.SET_STORE,
                payload: {
                  store: state,
                },
              })
            })
          })
          break
        }
        case 'SmartVLogoutProcess': {
          let state = getState()
          if (state.getIn(['isLogoutProcess'])) {
            return false
          }
          dispatch(clearBtnState())
          state = getState()
          let action = List()
          if (state.getIn(['project', 'Settings', 'ui', 'events', 'isUseBeforeLogoutEvent'], false)) {
            action = state.getIn(['project', 'Settings', 'ui', 'events', 'onBeforeLogOut'], List())
          }
          if (action.size === 0) {
            dispatch(clearState())
            state = getState()
          }
          dispatch({
            type: actionTypes.SET_STORE,
            payload: {
              store: state.setIn(['isLogoutProcess'], true),
            },
          })
          dispatch(
            setTargetParamsValue({
              params: {
                otpSalt: '',
                isOtpLogged: false,
              },
            })
          )

          let isFlow = false
          let isFlowFinish = false
          const finishNodesId = []
          action.map((fn) => {
            if (isImmutable(fn) && fn.get('modeType') === 'flow') {
              isFlow = true
            }
          })
          if (isFlow) {
            const nodes = action.getIn([0, 'actions', 'actionFlow', '0', 'list'], List())
            nodes.map((node) => {
              if (node.get('actionType') === 'flowFinish') {
                finishNodesId.push(node.get('id'))
              }
            })
            isFlowFinish = finishNodesId.length > 0
          }
          if (isFlow && isFlowFinish) {
            let nodes = action.getIn([0, 'actions', 'actionFlow', '0', 'list'], List())
            let sourceNodeConnections = nodes.filter((node) => finishNodesId.includes(node.get('target')))
            const logoutNode = {
              id: uuid(),
              type: 'action',
              actionType: 'SmartVProcessActualLogout',
              data: {
                uac: {
                  0: {
                    enabled: true,
                    action: 'SmartVProcessActualLogout',
                  },
                },
              },
            }
            nodes = nodes.push(fromJS(logoutNode))
            sourceNodeConnections.map((connOrigin) => {
              let connection = connOrigin.set('target', logoutNode.id)
              nodes = nodes.push(connection)
            })
            action = action.setIn([0, 'actions', 'actionFlow', '0', 'list'], nodes)
          } else {
            action = action.push(
              fromJS({
                uid: 'smartv-process-logout',
                conditions: [],
                isAction: false,
                actions: {
                  uac: {
                    0: {
                      enabled: true,
                      action: 'SmartVProcessActualLogout',
                    },
                  },
                },
              })
            )
          }
          dispatch(processButtonAction(action, 'SmartVEventProcess', false, null))
          break
        }
        case 'RefreshSmartVProfile': {
          axios.post(`/webprojects/profile/refresh`, {}).then(({ status, data }) => {
            if (data && data.status) {
              // window.__FT_APP_STATE__.user = data.username;
              dispatch(
                setTargetParamsValue({
                  params: data.retParams,
                  profileName: data.username,
                  profileImage: data.profileImage,
                })
              )
            }
          })
          break
        }
        case 'redirectToProfile': {
          try {
            let state = getState()
            const isPublic = state.getIn(['project', 'isPublic'], false)
            if (!isPublic) {
              let isUserSignIn = false
              try {
                isUserSignIn = Boolean(getPreviewDynamicData().user || getPreviewDynamicData().messageValue)
              } catch (err) {
                console.error(err)
              }
              if (isUserSignIn) {
                window.__FT_PROJECT__API__?.navigation?.profilePage?.()
              } else {
                window.__FT__PROCESS_REDIRECT = true
                try {
                  requestAnimationFrame(() => {
                    window.__FT_PROJECT__API__.goToPage('signin').then(() => {
                      window.__FT__PROCESS_REDIRECT = false
                    })
                  })
                } catch (error) {
                  window.__FT__PROCESS_REDIRECT = false
                }
              }
            }
          } catch (error) {
            console.error('cannot go to profile page', error)
          }
          break
        }
        case 'WorkFlow': {
          let paramsRequested = entry.getIn(['payload', 'paramsRequested'], Map())
          paramsRequested = isImmutable(paramsRequested) ? paramsRequested.toJS() : paramsRequested || {}
          processWorkFlow(
            entry.getIn(['payload', 'action']),
            entry.getIn(['payload', 'params'], Map()).toJS(),
            dispatch,
            getState,
            entry.getIn(['payload', 'testMode']),
            paramsRequested,
            entry.getIn(['payload', 'runInBackGround'], true),
            entry
          )
          break
        }
        case 'RefreshPickList': {
          let state = getState()
          dispatch(
            processWidgetSFMapping(
              'onload',
              null,
              null,
              null,
              true,
              state.getIn(['FTLang']),
              entry.getIn(['payload', 'list'], List()).toJS()
            )
          )
          break
        }
        case 'DocGenTitanSign': {
          processDocGenTitanSign(entry.getIn(['payload'], Map()).toJS(), dispatch, getState)
          break
        }
        case 'DocGen': {
          processDocGen(entry.getIn(['payload'], Map()).toJS(), dispatch, getState)
          break
        }
        case 'SfLWCRedirect': {
          processSfLWCRedirect(entry.getIn(['payload'], Map()).toJS(), dispatch)
          break
        }
        case 'slackMsg': {
          processSlackMessage(entry.getIn(['payload'], Map()).toJS())
          break
        }
        case 'ChatBotAIAction': {
          let nextStep = cache.filter((v) => v.get('type') == 'ChatBotNextMessage')
          processAIAction(entry.getIn(['payload', 'jobs'], Map()), nextStep, dispatch)
          break
        }
        case 'ChatBotNextMessage': {
          if (isChatAI) {
            break
          }
          if (typeof window?.__FT_PROJECT__API__?.forwardNextMessage === 'function') {
            window.__FT_PROJECT__API__.forwardNextMessage(entry.getIn(['payload', 'id'], ''))
          }
          break
        }
        case 'StepScrollAction': {
          StepScrollAction(entry, getState, dispatch)
          break
        }
        case 'close_loading_message': {
          if (window.closeNotifyHandler) {
            clearTimeout(window.closeNotifyHandler)
          }
          window.closeNotifyHandler = setTimeout(function () {
            dispatch({ type: actionTypes.CLOSE_LOADING_WINDOW })
          }, 300)
          break
        }
        case 'executeJS': {
          window.ftGetLocalIndex = entry.getIn(['payload', 'index'], null)
          const code = entry.getIn(['payload', 'code'], '')
          try {
            eval(code)
          } catch (err) {
            console.error(err)
          } finally {
            window.ftGetLocalIndex = null
          }
          break
        }
        case 'PageAction': {
          if (entry.getIn(['payload', 'action']) === 'Prev') {
            window.__FT_PROJECT__API__?.navigation?.prevPage?.()
          } else if (entry.getIn(['payload', 'action']) === 'Next') {
            window.__FT_PROJECT__API__?.navigation?.nextPage?.()
          }
          break
        }
        case 'AccordionAction': {
          const uid = entry.getIn(['payload', 'uid'], '')
          const action = entry.getIn(['payload', 'action'], List())
          dispatch(processButtonAction(action, uid, false, null))
          break
        }
        case 'TimerAction': {
          const id = entry.getIn(['payload', 'id'], '')
          const timerAction = entry.getIn(['payload', 'timerAction'], '')
          const action = toJS(entry.getIn(['payload', 'action'], Map()))
          const onFinish = toJS(entry.getIn(['payload', 'onFinish'], List()))
          const interval = entry.getIn(['payload', 'interval'], '')
          const repeat = entry.getIn(['payload', 'repeat'], '')
          const index = entry.getIn(['payload', 'index'])
          if (id && interval && timerAction) {
            window.__FT_PROJECT__API__?.startTimer?.({
              id,
              timerAction,
              action,
              interval,
              repeat,
              index,
              onFinish,
            })
          }
          break
        }
        case 'StepAction': {
          StepAction(entry, getState, dispatch)
          break
        }
        case 'TextReviewInteractivityAction': {
          processTextReviewInteractivityAction(entry, getState, dispatch)
          break
        }
        case 'markOnPage': {
          const value = entry.getIn(['payload', 'value'], '')
          import('mark.js')
            .then(({ default: Mark }) => {
              const contentMark = new Mark(document.getElementById('ft__builder__root__el'))
              contentMark.unmark({
                done: () => {
                  contentMark.mark(value)
                },
              })
            })
            .catch(console.error)
          break
        }
        case 'redirectToDisabled': {
          setTimeout(() => {
            window.__FT_PROJECT__API__.navigation.pageDisabled()
          }, 80)
          break
        }
        case 'set_focus': {
          set_focusAction(entry, getState, dispatch)
          break
        }
        case 'activateTab': {
          const uid = entry.getIn(['payload', 'uid'], '')
          const tabValue = entry.getIn(['payload', 'tab'], '')
          const isRepeat = entry.getIn(['payload', 'isRepeat'])
          const index = entry.getIn(['payload', 'index'])
          if (uid && tabValue && processedTabOnClick.indexOf(uid) === -1) {
            const state = getState()
            const layoutMode = state.get('layoutMode', 'lg')
            const pageUID = state
              .getIn(['project', 'Pages'], List())
              .find((page) => page.get('_id') === state.get('page'))
              ?.get?.('uid')
            const page = state.hasIn(['project', 'Settings', 'elements', uid]) ? 'project' : state.get('page')
            const el = state.getIn([...getElementDataRoot(page), 'elements', uid], Map())
            if (el.get('type') === 'Tab') {
              const tabs = getValueForMode(el, layoutMode, ['props'], ['tabs'], List())
              const metaInfo = state.getIn(['metaInfo', pageUID, uid], Map())
              if (el.getIn(['props', 'source'], 'static') === 'static' && !metaInfo.get('isDynamic')) {
                const tab = tabs.find((tab) => tab.get('value') === tabValue)
                if (tab) {
                  dispatch(processButtonAction(tab.get('action', List()), uid, isRepeat, index))
                  processedTabOnClick.push(uid)
                }
              } else {
                const tab = tabs.first()
                if (tab) {
                  dispatch(processButtonAction(tab.get('action', List()), uid, isRepeat, index))
                  processedTabOnClick.push(uid)
                }
              }
            }
          }
          break
        }
        case 'FocusErrorField': {
          try {
            const state = getState()
            const isPreview = true
            const layoutMode = state.get('layoutMode', 'lg')
            const index = entry.getIn(['payload', 'index'])
            const field = entry.getIn(['payload', 'field'], '')
            let repeatUID = null
            let isRepeat = false
            let [PageUID, uid, ...rest] = String(_.last(String(field).split('##'))).split(':')
            if (!uid) {
              uid = field
            }
            const PageID =
              state
                .getIn(['project', 'Pages'], List())
                .find((page) => page.get('uid') === PageUID)
                ?.get?.('_id') || state.get('page')
            if (state.getIn([...getElementDataRoot(PageID), 'elements', uid, 'type']) === 'Strip') {
              if (
                state.getIn(['project', 'Settings', 'RepeatedStripsID'], List()).findIndex((id) => id === uid) !== -1
              ) {
                repeatUID = uid
                uid = rest[0]
              }
            }
            let parentTab = findElementParentTab(PageID, uid, state)
            let childUID = uid
            while (parentTab) {
              const element = state.getIn([...getElementDataRoot(PageID), 'elements', parentTab], Map())
              const parent = element.get('parent')

              let metaInfo = state.getIn(['metaInfo', PageID, parentTab], Map())
              let metaChoices = state.getIn(['meta', PageID, parentTab], List())

              let tabRules = state.getIn(
                ['rules', PageUID, ...(isRepeat ? [repeatUID] : []), `${parentTab}-tab`, ...(isRepeat ? [index] : [])],
                Map()
              )

              let component = element
              const isStatic = component.getIn(['props', 'source'], 'static') === 'static'

              let choices
              let tabs = toJS(getValueForMode(component, layoutMode, ['props'], ['tabs'], List()))
              if (!_.isArray(tabs)) {
                tabs = []
              }
              if (isStatic && !isPreview) {
                choices = tabs
              } else if (isStatic && !metaInfo.get('isDynamic')) {
                choices = tabs
              } else {
                const children = _.get(_.first(tabs), 'children', []) || []
                choices = metaChoices
                  .map((v) => ({
                    label: v.get('label'),
                    id: v.get('value') || v.get('id'),
                    children,
                  }))
                  .toJS()
              }
              if (
                isPreview &&
                (component.getIn(['props', layoutMode, 'isRandomize']) ||
                  component.getIn(['props', layoutMode, 'isDistinct']))
              ) {
                const children = _.get(tabs, [0, 'children'], []) || []
                choices = _.uniqBy(
                  metaChoices
                    .map((v) => ({
                      label: v.get('label'),
                      id: v.get('value') || v.get('id'),
                      children,
                    }))
                    .toJS(),
                  'id'
                )
              }

              choices = _.filter(choices, (tab) => {
                if (tab) {
                  if (
                    tabRules.get(tab?.id, List()) &&
                    tabRules.get(tab?.id, List()).indexOf('hide') !== -1 &&
                    tabRules.get(tab?.id, List()).indexOf('show') === -1
                  ) {
                    return false
                  } else {
                    return true
                  }
                } else {
                  return false
                }
              })

              let tab = _.find(choices, (tab) => _.includes(tab.children, childUID))
              if (tab) {
                const value = fromJS({
                  ftListValue: _.get(tab, ['id']),
                  ftListLabel: _.get(tab, ['label']),
                })
                let uidPath = [parentTab]
                if (isRepeat) {
                  uidPath = [repeatUID, parentTab, index]
                }
                dispatch(setElemValue(uidPath, value, 'FormField', [PageUID === 'project' ? parent : PageUID]))
              }
              childUID = findElementParentTabChild(PageID, parentTab, state)
              parentTab = findElementParentTab(PageID, parentTab, state)
            }

            let freshState = getState()
            dispatch({
              type: actionTypes.SET_STORE,
              payload: {
                store: freshState.setIn(
                  ['__cache__'],
                  freshState.getIn(['__cache__'], List()).push(
                    fromJS({
                      type: 'set_focus',
                      payload: {
                        uid: uid,
                        index: index,
                        noScrollFix: true,
                      },
                    })
                  )
                ),
              },
            })
          } catch (error) {
            console.log(error)
          }
          break
        }
        case 'FormValidate': {
          const target = entry.getIn(['payload', 'target'], '')
          const mapToEl = entry.getIn(['payload', 'mapToEl'], '')
          const targetIndex = entry.getIn(['payload', 'index'])
          const [pageUID, ...rest] = String(target).split(':')
          const state = getState()
          const pageID = state
            .getIn(['project', 'Pages'], List())
            .find((page) => page.get('uid') === pageUID)
            ?.get?.('_id')
          const component = state.getIn(['pages', pageID, 'Props', 'elements', _.last(rest)], Map())
          let isRepeat = isParentRepeat(state, component.get('parent'))
          let repeatUID = getParentRepeatUID(state, component.get('parent'))
          const children = component.get('children', List())

          let index = null
          if (targetIndex !== undefined && targetIndex !== null) {
            index = parseInt(targetIndex, 10)
            index = _.isNaN(index) ? null : index
          }
          let errorsList = []
          const processValidate = (uid) => {
            let elem = state.getIn(['pages', pageID, 'Props', 'elements', uid], Map())
            if (elem.get('type') === 'FormField') {
              const control = elem.getIn(['props', 'control'], 'text')
              let isListCheckbox = false
              if (control == 'checkbox') {
                isListCheckbox = component.getIn(['props', 'source'], 'single') !== 'single'
              }
              const getRepeatPath = (prefix = 'state', isFullValue) => {
                let propName = []
                if (control === 'currency' && prefix != 'formFieldStatuses') {
                  propName = ['value']
                }
                if ((LIST_BASED_FIELDS.indexOf(control) > -1 || isListCheckbox) && prefix == 'state' && !isFullValue) {
                  propName = ['ftListValue']
                }
                return pageUID === 'project'
                  ? isRepeat
                    ? [prefix, parent, repeatUID, uid, ...propName, String(index)]
                    : [prefix, parent, uid, ...propName]
                  : isRepeat
                    ? [prefix, pageUID, repeatUID, uid, ...propName, String(index)]
                    : [prefix, pageUID, uid, ...propName]
              }
              let ruleStatus = dispatch(processValueRules(uid, elem, getRepeatPath, index))
              if (ruleStatus.status) {
                errorsList.push(ruleStatus.error)
              }
            }
          }

          if (isRepeat) {
            if (targetIndex !== undefined && targetIndex !== null) {
              index = parseInt(targetIndex, 10)
              index = _.isNaN(index) ? null : index
              children.map(processValidate)
            } else {
              let repeatState = state.getIn(['state', pageUID, repeatUID], Map())
              let repeatCount = getMappingCount(repeatState.toJS())
              _.times(repeatCount, (repeatIndex) => {
                index = repeatIndex
                children.map(processValidate)
              })
            }
          } else {
            children.map(processValidate)
          }
          if (mapToEl) {
            dispatch({
              type: actionTypes.COND_RULES_SET_STATUS_TO_FIELD,
              payload: {
                mapToEl: mapToEl,
                status: errorsList,
              },
            })
          }
          break
        }
        case 'FormSubmit': {
          const target = entry.getIn(['payload', 'target'], '')
          const targetIndex = entry.getIn(['payload', 'index'])
          const [pageUID, ...rest] = String(target).split(':')
          const state = getState()
          const pageID = state
            .getIn(['project', 'Pages'], List())
            .find((page) => page.get('uid') === pageUID)
            ?.get?.('_id')
          const component = state.getIn(['pages', pageID, 'Props', 'elements', _.last(rest)], Map())
          let isValid = true
          if (!getValueForMode(component, state.get('layoutMode', 'lg'), ['props'], ['ignoreValidation'], false)) {
            let isRepeat = isParentRepeat(state, component.get('parent'))
            let repeatUID = getParentRepeatUID(state, component.get('parent'))
            const children = component.get('children', List())

            let index = null
            if (targetIndex !== undefined && targetIndex !== null) {
              index = parseInt(targetIndex, 10)
              index = _.isNaN(index) ? null : index
            }
            const processValidate = (uid) => {
              let elem = state.getIn(['pages', pageID, 'Props', 'elements', uid], Map())
              if (elem.get('type') === 'FormField') {
                const control = elem.getIn(['props', 'control'], 'text')
                let isListCheckbox = false
                if (control == 'checkbox') {
                  isListCheckbox = component.getIn(['props', 'source'], 'single') !== 'single'
                }
                const getRepeatPath = (prefix = 'state', isFullValue) => {
                  let propName = []
                  if (control === 'currency' && prefix != 'formFieldStatuses') {
                    propName = ['value']
                  }
                  if (
                    (LIST_BASED_FIELDS.indexOf(control) > -1 || isListCheckbox) &&
                    prefix == 'state' &&
                    !isFullValue
                  ) {
                    propName = ['ftListValue']
                  }
                  return pageUID === 'project'
                    ? isRepeat
                      ? [prefix, parent, repeatUID, uid, ...propName, String(index)]
                      : [prefix, parent, uid, ...propName]
                    : isRepeat
                      ? [prefix, pageUID, repeatUID, uid, ...propName, String(index)]
                      : [prefix, pageUID, uid, ...propName]
                }
                let v = dispatch(processValueRules(uid, elem, getRepeatPath, index))
                if (_.get(v, 'ruleStatus.status', 'initial') === 'error') {
                  isValid = false
                }
              }
            }

            if (isRepeat) {
              if (targetIndex !== undefined && targetIndex !== null) {
                index = parseInt(targetIndex, 10)
                index = _.isNaN(index) ? null : index
                children.map(processValidate)
              } else {
                let repeatState = state.getIn(['state', pageUID, repeatUID], Map())
                let repeatCount = getMappingCount(repeatState.toJS())
                _.times(repeatCount, (repeatIndex) => {
                  index = repeatIndex
                  children.map(processValidate)
                })
              }
            } else {
              children.map(processValidate)
            }
          }
          if (isValid) {
            dispatch(
              processButtonAction(component.getIn(['props', 'onSubmit', 'action'], Map()).toJS(), _.last(rest), [])
            )
          }
          break
        }
        case 'FTSetParameters':
          try {
            if (
              window.parent &&
              window.parent.__FT_PROJECT__API__ &&
              window.parent.__FT_PROJECT__API__.FTSetParameters
            ) {
              const state = getState()

              let params = getParams(state)
              let portalState = getStateValues(state)
              let requestParams = {}
              let parentParams = entry.getIn(['payload', 'parentParams'], Map())
              parentParams.map(function (param) {
                if (param.get('valueType') == 'static') {
                  requestParams[param.get('name')] = param.get('value')
                } else if (param.get('valueType') == 'field') {
                  requestParams[param.get('name')] = getValueByField(
                    param.get('fieldValue', Map()),
                    params,
                    portalState,
                    param.get('index', null)
                  )
                }
              })

              window.parent.__FT_PROJECT__API__.FTSetParameters(requestParams)
            }
          } catch (err) {
            console.error(err)
          }
          break
        case 'FTCloseModal':
          try {
            const state = getState()
            let params = state.get('params').toJS()
            cache.map((entry) => {
              if (entry.get('type') == 'FTSetParameters') {
                try {
                  entry.getIn(['payload', 'parentParams'], Map()).map((row) => {
                    if (row) {
                      delete params[row.get('name')]
                    }
                  })
                } catch (err) {}
              }
            })
            dispatch(ftCloseModal(params))
          } catch (err) {
            console.error(err)
          }
          break
        case 'downloadFileModal': {
          let files = List.isList(entry.getIn(['payload', 'recordID'], ''))
            ? entry.getIn(['payload', 'recordID'], '').toJS()
            : String(entry.getIn(['payload', 'recordID'], '')).split(',')
          let state = getState()
          state = state.setIn(
            ['widgets', 'ftDownModal'],
            fromJS({
              show: true,
              files: files,
              type: entry.getIn(['payload', 'type'], ''),
            })
          )
          dispatch({
            type: actionTypes.SET_STORE,
            payload: {
              store: state,
            },
          })
          break
        }
        case 'downloadFile':
          try {
            if (window.lastDownloadFileTime) {
              let currentTime = new Date().getTime() / 1000
              if (currentTime - window.lastDownloadFileTime < 3) {
                console.error('Download file rate limit')
                break
              }
            }
            window.lastDownloadFileTime = new Date().getTime() / 1000
            axios
              .post(
                `/webprojects/get/downloadFile`,
                {
                  recordID: entry.getIn(['payload', 'recordID'], ''),
                  type: entry.getIn(['payload', 'type'], ''),
                },
                { responseType: 'blob' }
              )
              .then(function (result) {
                if (result.status == 200) {
                  let fileName = ''
                  if (result.headers && result.headers['content-disposition']) {
                    fileName = result.headers['content-disposition'].split('=')[1]
                    fileName = decodeURIComponent(fileName)
                  } else {
                  }
                  if (typeof window.navigator.msSaveBlob !== 'undefined') {
                    window.navigator.msSaveBlob(result.data, fileName)
                  } else {
                    var URL = window.URL || window.webkitURL
                    var downloadUrl = URL.createObjectURL(result.data)

                    if (fileName) {
                      // use HTML5 a[download] attribute to specify filename
                      var a = document.createElement('a')
                      // safari doesn't support this yet
                      if (typeof a.download === 'undefined') {
                        window.location = downloadUrl
                      } else {
                        a.href = downloadUrl
                        a.download = fileName
                        document.body.appendChild(a)
                        a.click()
                      }
                    } else {
                      window.location = downloadUrl
                    }

                    setTimeout(function () {
                      URL.revokeObjectURL(downloadUrl)
                    }, 100) // cleanup
                  }
                }
              })
              .catch(function (err) {
                console.error(err)
              })
          } catch (err) {
            console.error(err)
          }

          break
      }
    } catch (err) {
      console.error(err)
    }
  })
}
